<script lang="ts">
  /* Product list — redesigned for Batch 2.
     Compact filters above table, configurable columns, quick visibility toggle,
     and per-row view/edit actions.

     2026-09-13 («одна база данных — унифицируем»): the first filter row is the
     one «Доступ позиций», Prices and Склад share — search, category,
     subcategory, supplier, «Настройка формы экрана» — and this page's own
     options (column preset, status, sort, in stock) are a second row under it.
     «Настройка формы экрана» holds every field of the card, and every one of
     them edits right in the list: typed, ticked, or picked from the same
     reference lists the card uses. Only the figures the system works out
     (CZK price, stock, pallet totals) stay read-only.

     2026-09-19 (owner): Продукты is the second of four views of the «Склад»
     section ($components/CatalogHead). The table edits only in «Редакция»;
     «Сводная» reads it without a cell that moves under the mouse. The column
     headers sort (the A-Z picker went), «Только в наличии» joined the status
     picker (Все / Активные / В наличии), the pencil went — the eye opens the
     card, and the card is edited there — and «В архив», which left Склад, is
     a column of «Редакция» here. The two ticks and the eye are always on the
     row, outside «Настройка формы экрана». */
  import { onMount, untrack } from "svelte";
  import { push, replace, router } from "svelte-spa-router";
  import { _, locale } from "svelte-i18n";
  import AppShell from "$components/AppShell.svelte";
  import CatalogHead, { loadCatalogMode, type CatalogMode } from "$components/CatalogHead.svelte";
  import CatalogFilterBar, { type FilterChange } from "$components/CatalogFilterBar.svelte";
  import ColumnSettings, { type ColumnDef } from "$components/ColumnSettings.svelte";
  import LoadErrorState from "$components/LoadErrorState.svelte";
  import InfoTooltip from "$components/InfoTooltip.svelte";
  import SectionGuide from "$components/SectionGuide.svelte";
  import SetupHint from "$components/SetupHint.svelte";
  import ProductCategories from "./ProductCategories.svelte";
  import { requestUpgrade } from "$lib/api";
  import { fetchSupplierNames } from "$lib/suppliers";
  import { localizedCountryName } from "$lib/countries";
  import { auth } from "$lib/auth";
  import { knownCategories, loadCatalogFilters, saveCatalogFilters } from "$lib/catalogFilters";
  import { canAction, can, hasEntitlement } from "$lib/permissions";
  import { hasSurface } from "$lib/surfaces";
  import { formatBarcode, formatDate, formatEur, priceDecimals } from "$lib/format";
  import { prefKey, readPref, writePref } from "$lib/prefs";
  import {
    bulkUploadProductImagesBatched,
    type BulkImageResult,
    fetchAllergens,
    fetchCategories,
    fetchCountries,
    fetchLabelIcons,
    fetchPalletTypes,
    palletLabel,
    palletWeightOptions,
    fetchProducts,
    fetchProductStandards,
    fetchProductSuppliers,
    updateProduct,
    setProductFlags,
    weightScaleForUnit,
    scaleToDisplay,
    scaleToStored,
    type Category,
    type CountryRef,
    type PalletTypeRef,
    type ProductListItem,
    fetchProductMarks,
    type ProductMark,
  } from "$lib/catalog";

  import SupplierUpdates from "./SupplierUpdates.svelte";
  import SupplierGapDialog from "./SupplierGapDialog.svelte";
  import SupplierGapBadge from "$components/SupplierGapBadge.svelte";
  import {
    fetchPendingSupplierUpdates,
    fetchSupplierOffers,
    resolveAllSupplierUpdates,
    resolveSupplierBack,
    type ProductUpdates,
  } from "$lib/supplierDirectory";
  import { productFieldLabel } from "$lib/productFieldLabels";
  import {
    RECEIPT_MARKS,
    alcoholTypeName,
    archiveProduct,
    fetchAlcoholTypes,
    fetchStampSuppliers,
    fetchStockSettings,
    type AlcoholType,
    type ReceiptMark,
    type StampSupplier,
  } from "$lib/warehouse";
  import { columnWidths } from "$lib/columnWidths.svelte";

  let { params }: { params: { slug: string } } = $props();
  const slug = $derived(params?.slug ?? "");
  const canEditProducts = $derived(can($auth, slug, "products", "edit"));
  /* The tree is its own permission and its own surface — the same two gates
     that used to decide whether the sidebar drew «Категории товаров». */
  const canViewCategories = $derived(
    hasSurface($auth, slug, "nav.product-categories") && can($auth, slug, "categories", "view"),
  );

  let categories = $state<Category[]>([]);
  let products = $state<ProductListItem[]>([]);
  let total = $state(0);
  let loading = $state(true);
  // A failed load must render as an error+retry, never as an empty catalog
  // (audit 2026-08-17, OE-A06).
  let loadError = $state(false);

  /* Reference lists for the cells that pick rather than type (2026-09-13):
     country, pallet type, allergens, standards, label icons — the lists the
     card itself picks from. Loaded in the background after the list: one that
     fails leaves its cells read-only rather than the page blank. */
  type Ref = { id: number; code: string; name: string };
  let countriesRef = $state<CountryRef[]>([]);
  let palletTypesRef = $state<PalletTypeRef[]>([]);
  let allergensRef = $state<Ref[]>([]);
  let standardsRef = $state<Ref[]>([]);
  let iconsRef = $state<Ref[]>([]);
  /* «Акцизный товар → Алкоголь» (2026-09-29): the type registry, the types
     the company ticked (null until the settings answer) and its stamp
     suppliers — what the alcohol cells pick from. */
  let alcoholTypesRef = $state<AlcoholType[]>([]);
  let alcoholTicked = $state<string[] | null>(null);
  let stampSuppliersRef = $state<StampSupplier[]>([]);

  /* Filters. Search, category, subcategory and supplier are shared with
     «Доступ позиций», every Prices tab and Склад (2026-09-11, widened
     2026-09-13): the page opens on what was last picked on any of them and
     writes its own changes back. */
  /* ?supplier=<name>: opened from a supplier's row in «Мои партнёры» — the
     supplier's catalogue is our cards tagged with it, worked here with every
     way of adding goods (owner, 2026-09-22). That supplier alone, not on top
     of whatever search or category was left picked. */
  const qsSupplier = untrack(() =>
    (new URLSearchParams(router.querystring ?? "").get("supplier") ?? "").trim(),
  );
  const sharedFilters = untrack(() =>
    qsSupplier
      ? { q: "", supplier: qsSupplier, category: null, subcategory: null }
      : loadCatalogFilters($auth.user?.id, slug),
  );
  let searchQ = $state(sharedFilters.q);
  let selectedCategory = $state<number | null>(sharedFilters.category);
  let selectedSubcategory = $state<number | null>(sharedFilters.subcategory);
  let selectedSupplier = $state(sharedFilters.supplier);
  // Top-level groups, for the inline group dropdown (Task 5.1).
  const parentCategories = $derived(categories.filter((c) => c.parent_id == null));
  /** «Все признаки»: only the cards carrying one accounting mark, as on Склад. */
  let mark = $state<ReceiptMark | string>("");
  /** Marks the company added itself, offered after the built-in ones. */
  let customMarks = $state<ProductMark[]>([]);
  /* The sugar excise is Latvian; elsewhere nobody records it. */
  const isLv = $derived(
    ($auth.memberships.find((m) => m.tenant.slug === slug)?.tenant.country ?? "").toUpperCase() === "LV",
  );
  const markOptions = $derived(RECEIPT_MARKS.filter((m) => m !== "sugar" || isLv || mark === "sugar"));
  /* «Все / Активные / В наличии / Архив» — everything, the working
     assortment, what is actually on the shelf (owner, 2026-09-19: the
     in-stock tick joined this picker), and the archive alone (2026-09-22).
     A card comes back out of the archive by unticking «В архив» under
     «Редакция». */
  type Status = "all" | "active" | "in_stock" | "archived";
  let status = $state<Status>("active");
  let page = $state(1);
  /* Supplier filter — every supplier in «Мои поставщики» (owner,
     2026-09-22: all four active partners belong here, cards or not), plus
     any other name the cards carry. */
  let suppliers = $state<string[]>([]);
  /* «Мои поставщики» — partner links and directory records — for the inline
     supplier dropdown: the supplier is picked, not typed. Empty only when
     the company has no suppliers at all: those cells keep the free-text
     input. */
  let dirSuppliers = $state<string[]>([]);
  $effect(() => {
    saveCatalogFilters($auth.user?.id, slug, {
      q: searchQ,
      supplier: selectedSupplier,
      category: selectedCategory,
      subcategory: selectedSubcategory,
    });
  });

  /* The supplier the filter is on, handed to «Добавить продукт» and «Импорт»
     so what they create is that supplier's from the start. */
  const supplierQs = $derived(selectedSupplier ? `?supplier=${encodeURIComponent(selectedSupplier)}` : "");

  /* «Таблица / Каталог» (owner, 2026-09-22): the same cards as a grid of
     tiles with the photo, the way a supplier's catalogue reads. Remembered
     per user and company. */
  type Layout = "table" | "catalog";
  const layoutKey = untrack(() => prefKey("products.layout", $auth.user?.id, slug));
  let layout = $state<Layout>(readPref<Layout>(layoutKey) === "catalog" ? "catalog" : "table");
  $effect(() => writePref(layoutKey, layout));

  // UI States
  /* «Редакция / Сводная»: cells edit only in «Редакция». */
  let mode = $state<CatalogMode>(untrack(() => loadCatalogMode("products", $auth.user?.id, slug)));
  const editing = $derived(mode === "edit");
  let columnsOpen = $state(false);
  let togglingId = $state<number | null>(null);
  let toast = $state("");

  /* Page tabs: the category tree the cards are filed in, the product list
     itself, and the review of supplier edits to linked cards («Обновления от
     поставщиков»). The badge is loaded up front so pending changes are
     visible without opening the tab.

     «Категории товаров» left the sidebar on 2026-09-16 and opens here, first
     of the three — its old URL still works and lands on that tab. */
  type View = "categories" | "list" | "updates";
  let view = $state<View>(
    typeof window !== "undefined" && window.location.hash.includes("/product-categories")
      ? "categories"
      : "list",
  );
  $effect(() => {
    if (view === "categories" && !canViewCategories) view = "list";
  });

  /* Supplier → our cards (spec 20260915_supplier_access_sync): the list
     narrowed from the dashboard hint or the e-mail link (?gap=pending), the
     decision dialog for positions the supplier no longer offers, and the
     supplier's pending card edits keyed by product for the strip under a row. */
  let gapFilter = $state<"" | "pending" | "any" | "back">("");
  let gapDialogIds = $state<number[] | null>(null);
  let pendingUpdates = $state<ProductUpdates[]>([]);
  const updatesByProduct = $derived(new Map(pendingUpdates.map((u) => [u.product_id, u])));
  let rowBusy = $state<number | null>(null);
  const qsGap = $derived(new URLSearchParams(router.querystring ?? "").get("gap") ?? "");
  let updatesCount = $state(0);

  /* Thumbnail hover preview. A 40px thumbnail is enough to tell "has a photo"
     apart from "hasn't", and nothing else — hovering shows the picture at a
     size you can actually judge, without leaving the list. */
  const PREVIEW_MAX = 320;
  let imgPreview = $state<{ url: string; x: number; y: number } | null>(null);

  function showImgPreview(e: MouseEvent, url: string) {
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // Beside the thumbnail, flipped to its left when there's no room on the
    // right, and clamped so a row near either edge still shows the whole card.
    const x =
      r.right + 12 + PREVIEW_MAX <= window.innerWidth
        ? r.right + 12
        : Math.max(8, r.left - 12 - PREVIEW_MAX);
    const y = Math.min(
      Math.max(8, r.top + r.height / 2 - PREVIEW_MAX / 2),
      Math.max(8, window.innerHeight - PREVIEW_MAX - 8),
    );
    imgPreview = { url, x, y };
  }
  function hideImgPreview() {
    imgPreview = null;
  }

  // --- Columns: spec-driven, inline-editable, with Mandatory/All presets ----
  /* How a cell edits:
       text / num / dec — typed;  long — a text box (description, composition…);
       cat     — group / subgroup dropdowns over the Product Categories tree;
       select  — a fixed list (`options`): unit, volume unit, brand, Incoterms;
       country / pallet — picked from the reference lists;
       langs   — label languages typed as codes, «EE, LV, LT»;
       multi   — allergens / standards / label icons, ticked in a popover;
       bool    — a tick, saved as it is clicked;
       ro      — worked out by the system, never typed. */
  type ColType =
    | "text" | "num" | "dec" | "long" | "cat" | "select" | "country" | "pallet"
    | "langs" | "multi" | "bool" | "ro";
  type Col = {
    key: string;
    label: string;
    field?: string;
    type: ColType;
    mandatory?: boolean;
    locked?: boolean;
    /** A column the database cannot hold empty: a blank entry is not saved. */
    required?: boolean;
    options?: { value: string; label: string }[];
  };

  /* Which section of the product card each column belongs to. The picker
     groups by this, in card order, so a field is looked up where it is filled
     in rather than by scanning the whole list. Keys absent from a group land
     under the last heading of the run they sit in.

     Only the card's main sections are offered (2026-09-14): composition &
     storage and «Другое» are secondary, so their fields never join the
     «Выбранные» set — they show, and edit, under «Все поля» alone. */
  const ALL_FIELDS_ONLY = new Set([
    // Состав и хранение
    // «sugars» is kept: the sugar-excise section offers it (2026-09-29).
    "composition", "allergens", "kcal", "kj", "fat", "saturatedFat", "carbs",
    "protein", "salt", "tempRegime", "humidityMax", "storageConditions",
    "afterOpeningConditions", "afterOpeningHours", "needsTempControl",
    // Другое
    "bicarbonates", "sulfates", "chlorides", "calcium", "magnesium",
    "sodium", "potassium", "vitaminC", "vitaminD",
    "egais", "aceticAcidPct", "isSupplement",
    "languages", "standards", "labelIcons",
    // Taken off the picker by the owner (2026-09-28): export and Incoterms
    // are the invoice's business, «Поддон» / lead time / min remaining shelf
    // life are rarely read in the list.
    "hasExport", "incoterms", "pallet", "leadTime", "minRemainingShelfLife",
    // 2026-09-29: invoice and CMR add up the gross weight from the lines
    // themselves; the card's per-pallet total is not a list column.
    "totalGross",
  ]);
  const withoutAllFieldsOnly = (vis: Record<string, boolean>) =>
    Object.fromEntries(Object.entries(vis).map(([k, v]) => [k, v && !ALL_FIELDS_ONLY.has(k)]));
  /* The picker's sections, in the order the owner laid them out (2026-09-28):
     Основное opens with the photo (the «Фотографии» heading is gone), then
     Общая информация and Логистика, each row for row with ProductDetail's
     `fieldsFor` / logistics blocks. Columns the card has no row for — stock,
     the nearest best-before, total gross — sit where they are read.

     The accounting marks come last, one section each, and only for the marks
     the company keeps (Настройки → Склад → Учётные признаки). Each repeats
     «Объём»: the same column, offered where the mark is thought about. */
  type CardGroup = { label: string; keys: string[]; mark?: ReceiptMark };
  const MARK_GROUPS: { mark: ReceiptMark; keys: string[] }[] = [
    {
      mark: "alcohol",
      keys: ["isAlcohol", "alcoholType", "alcoholPct", "litres", "exciseStampSupplier", "exciseStampRange"],
    },
    { mark: "deposit", keys: ["deposit", "volume"] },
    // Coffee and sugar drinks are declared in litres; a drink's band is its
    // sugar per 100 g (2026-09-29).
    { mark: "coffee", keys: ["isCoffee", "litres"] },
    { mark: "sugar", keys: ["sugarExcise", "sugars", "litres"] },
  ];
  /* Columns more than one mark shows, or the card's own figures (volume,
     litrage, sugar) — never hidden with a mark. */
  const SHARED_KEYS = new Set(["volume", "litres", "sugars"]);
  /** Columns that belong to a mark alone — hidden when the mark is not kept. */
  const MARK_ONLY_KEYS = new Map(
    MARK_GROUPS.flatMap((g) => g.keys.filter((k) => !SHARED_KEYS.has(k)).map((k) => [k, g.mark] as const)),
  );
  /* The marks this company keeps. Null until the settings answer; a failed
     read shows every mark rather than hiding columns someone relies on. */
  let keptMarks = $state<ReceiptMark[] | null>(null);
  const markKept = (m: ReceiptMark) => keptMarks === null || keptMarks.includes(m);
  /** A column the table may show: ticked, and not a mark the company dropped. */
  const colOn = (key: string) => {
    if (!colVisible[key]) return false;
    if (key === "alcoholType" && !typesTicked()) return false;
    const m = MARK_ONLY_KEYS.get(key);
    return !m || markKept(m);
  };
  /** «Вид алкоголя» has something to offer: a type is ticked in Настройки →
   *  Склад → Акцизный товар (or the settings have not answered yet). */
  const typesTicked = () => alcoholTicked === null || alcoholTicked.length > 0;
  const CARD_GROUPS: CardGroup[] = $derived([
    {
      label: $_("pf.tab.basics"),
      keys: [
        "image", "name", "ean", "sku", "priceEur", "vat", "stock",
        "pcsPerDb", "pcsPerBox",
        "unit", "netWeight", "grossWeight",
        "volumeUnit", "volume",
        "minOrder", "shelfLifeDays", "expiresOn",
      ],
    },
    {
      label: $_("pf.tab.general"),
      keys: [
        "group", "subgroup", "description", "brand", "brandOwnership",
        "manufacturer", "supplier", "partnerNote", "country",
      ],
    },
    {
      label: $_("pf.tab.logistics"),
      keys: [
        "customs",
        "length", "width", "height",
        "dbLength", "dbWidth", "dbHeight", "dbPerBox",
        "boxLength", "boxWidth", "boxHeight", "boxesPerLayer",
        "pcsPerPallet", "boxesPerPallet", "layersPerPallet",
        "palletType", "palletWeight", "palletAllowedHeight",
        "cargoNature",
      ],
    },
    ...MARK_GROUPS.filter((g) => markKept(g.mark)).map((g) => ({
      label: $_("pl.columns.markGroup", { values: { mark: $_(`receipt.mark.${g.mark}`) } }),
      keys: g.keys.filter((k) => k !== "alcoholType" || typesTicked()),
      mark: g.mark,
    })),
  ]);
  const GROUP_OF = $derived(
    new Map(CARD_GROUPS.flatMap((g) => g.keys.map((k) => [k, g.label] as const))),
  );

  /* The card's weight units only (g/kg): a card can't *weigh* millilitres or
     pieces. A legacy card still on ml/l/pcs/m keeps its value as an extra
     option in the dropdown until someone switches it. */
  const UNIT_OPTIONS = [{ value: "g", label: "g" }, { value: "kg", label: "kg" }];

  // `field` = ProductListItem key to inline-edit (omit / type "ro" = read-only cell).
  // `mandatory` mirrors the spec's "Обязательные поля" row → the Mandatory preset.
  // The full set below covers every field of the card (the "All fields"
  // preset). Packaging materials and the characteristic sections are tables
  // of their own and stay on the card. Read-only cells still carry `field` so
  // cellText can read the value.
  /* The ticked types, in the registry's order (the VID categories first),
     plus any a card on screen already carries — a type unticked later stays
     readable on the cards that have it. */
  const alcoholTypeOptions = $derived(
    alcoholTypesRef
      .filter((t) => (alcoholTicked ?? []).includes(t.code) || products.some((p) => p.alcohol_type === t.code))
      .map((t) => ({ value: t.code, label: alcoholTypeName(t, $locale) })),
  );
  const unitSub = $derived($_("pf.sub.unit"));
  const dispBox = $derived($_("pf.sub.displayBox"));
  const transBox = $derived($_("pf.sub.transportBox"));
  const COLS = $derived<Col[]>([
    { key: "image", label: $_("pl.col.image"), type: "ro" },
    { key: "name", label: $_("pf.f.title"), field: "name", type: "text", mandatory: true, locked: true, required: true },
    { key: "sku", label: $_("pf.f.localCode"), field: "sku", type: "text", mandatory: true },
    { key: "ean", label: $_("pf.f.barcode"), field: "ean", type: "text", mandatory: true },
    { key: "group", label: $_("pf.f.category"), type: "cat", mandatory: true },
    { key: "subgroup", label: $_("pf.f.subcategory"), type: "cat", mandatory: true },
    { key: "manufacturer", label: $_("pf.f.manufacturer"), field: "manufacturer", type: "text", mandatory: true },
    { key: "supplier", label: $_("pf.f.supplier"), field: "supplier", type: "text" },
    { key: "brand", label: $_("pf.f.brand"), field: "brand", type: "text", mandatory: true },
    {
      key: "brandOwnership", label: $_("pf.f.brandOwnership"), field: "brand_ownership", type: "select", required: true,
      options: [
        { value: "own", label: $_("pl.ownership.own") },
        { value: "third_party", label: $_("pl.ownership.third_party") },
      ],
    },
    { key: "country", label: $_("pf.f.originCountry"), field: "origin_country_id", type: "country", mandatory: true },
    { key: "customs", label: $_("pf.f.customsCode"), field: "customs_code", type: "text" },
    { key: "hasExport", label: $_("pf.f.hasExport"), field: "has_export", type: "bool" },
    {
      key: "incoterms", label: $_("pf.f.incoterms"), field: "incoterms", type: "select",
      options: [{ value: "", label: "-" }, { value: "EXW", label: "EXW" }, { value: "DDP", label: "DDP" }],
    },
    { key: "partnerNote", label: $_("pf.f.partnerNote"), field: "partner_note", type: "long" },
    { key: "description", label: $_("pd.fields.description"), field: "description", type: "long" },
    { key: "composition", label: $_("pf.f.composition"), field: "composition", type: "long" },
    { key: "allergens", label: $_("pf.tab.allergens"), field: "allergen_ids", type: "multi" },
    { key: "unit", label: $_("pf.f.unit"), field: "unit", type: "select", mandatory: true, required: true, options: UNIT_OPTIONS },
    {
      key: "volumeUnit", label: $_("pf.f.volumeUnit"), field: "volume_unit", type: "select",
      options: [{ value: "", label: "-" }, { value: "l", label: "l" }, { value: "ml", label: "ml" }],
    },
    { key: "volume", label: $_("pf.f.volume"), field: "volume_value", type: "dec" },
    { key: "minOrder", label: $_("pd.minOrderLabel"), field: "min_order_qty", type: "num", required: true },
    /* price_eur IS the base sale price the Prices tab edits, so it carries
       the same name here (owner, 2026-09-16). The CZK column that used to
       follow it — the same figure through the day's ECB rate, left over from
       the Czech-market first draft — is gone with the same feedback. */
    { key: "priceEur", label: $_("prices.col.basePrice"), field: "price_eur", type: "dec", required: true },
    { key: "vat", label: $_("pd.fields.vatRate"), field: "vat_rate", type: "dec", required: true },
    /* «Упаковка» (packaging_qty) left the card UI, so it left this screen's
       layout picker too (2026-08-14). The field itself survives on the model —
       imports and box-multiple ordering still read it. */
    { key: "pallet", label: $_("pl.col.pallet"), field: "pallet_qty", type: "num" },
    { key: "stock", label: $_("pl.col.stock"), type: "ro" },
    { key: "expiresOn", label: $_("ledger.receipt.expiresOn"), field: "nearest_expires_on", type: "ro" },
    // Nutrition (per 100 g·ml)
    { key: "kcal", label: $_("pf.f.kcal"), field: "energy_kcal", type: "dec" },
    { key: "kj", label: $_("pf.f.kj"), field: "energy_kj", type: "dec" },
    { key: "fat", label: $_("pf.f.fat"), field: "fat_g", type: "dec" },
    { key: "saturatedFat", label: $_("pf.f.saturatedFat"), field: "saturated_fat_g", type: "dec" },
    { key: "carbs", label: $_("pf.f.carbs"), field: "carbs_g", type: "dec" },
    { key: "sugars", label: $_("pf.f.sugars"), field: "sugars_g", type: "dec" },
    { key: "protein", label: $_("pf.f.protein"), field: "protein_g", type: "dec" },
    { key: "salt", label: $_("pf.f.salt"), field: "salt_g", type: "dec" },
    // Additional
    { key: "egais", label: $_("pf.f.egais"), field: "egais_code", type: "text" },
    { key: "alcoholPct", label: $_("pf.f.alcoholPct"), field: "alcohol_pct", type: "dec" },
    { key: "aceticAcidPct", label: $_("pf.f.aceticAcidPct"), field: "acetic_acid_pct", type: "dec" },
    { key: "isSupplement", label: $_("pf.f.isSupplement"), field: "is_supplement", type: "bool" },
    { key: "standards", label: $_("pf.tab.standards"), field: "standard_ids", type: "multi" },
    { key: "labelIcons", label: $_("pf.tab.icons"), field: "label_icon_ids", type: "multi" },
    // Minerals & vitamins
    { key: "bicarbonates", label: $_("pf.f.bicarbonates"), field: "bicarbonates", type: "dec" },
    { key: "sulfates", label: $_("pf.f.sulfates"), field: "sulfates", type: "dec" },
    { key: "chlorides", label: $_("pf.f.chlorides"), field: "chlorides", type: "dec" },
    { key: "calcium", label: $_("pf.f.calcium"), field: "calcium", type: "dec" },
    { key: "magnesium", label: $_("pf.f.magnesium"), field: "magnesium", type: "dec" },
    { key: "sodium", label: $_("pf.f.sodium"), field: "sodium", type: "dec" },
    { key: "potassium", label: $_("pf.f.potassium"), field: "potassium", type: "dec" },
    { key: "vitaminC", label: $_("pf.f.vitaminC"), field: "vitamin_c_mg", type: "dec" },
    { key: "vitaminD", label: $_("pf.f.vitaminD"), field: "vitamin_d", type: "dec" },
    // Shelf life & storage
    { key: "shelfLifeDays", label: $_("pf.f.shelfLifeDays"), field: "shelf_life_days", type: "num" },
    { key: "minRemainingShelfLife", label: $_("pf.f.minRemainingShelfLife"), field: "min_remaining_shelf_life_pct", type: "num" },
    { key: "tempRegime", label: $_("pf.f.tempRegime"), field: "storage_temp_regime", type: "text" },
    { key: "humidityMax", label: $_("pf.f.humidityMax"), field: "humidity_max_pct", type: "num" },
    { key: "afterOpeningConditions", label: $_("pf.f.afterOpeningConditions"), field: "after_opening_conditions", type: "text" },
    { key: "afterOpeningHours", label: $_("pf.f.afterOpeningHours"), field: "after_opening_hours", type: "num" },
    { key: "needsTempControl", label: $_("pf.f.needsTempControl"), field: "needs_temp_control", type: "bool" },
    { key: "storageConditions", label: $_("pf.f.storageConditions"), field: "storage_conditions", type: "text" },
    { key: "leadTime", label: $_("pd.param.leadTime"), field: "lead_time_days", type: "num", required: true },
    // Label
    { key: "languages", label: $_("pf.f.languages"), field: "packaging_languages", type: "langs" },
    { key: "deposit", label: $_("pf.f.deposit"), field: "has_deposit", type: "bool" },
    // Accounting marks (2026-09-16)
    { key: "sugarExcise", label: $_("pf.f.sugarExcise"), field: "sugar_excise", type: "bool" },
    { key: "isAlcohol", label: $_("pf.f.isAlcohol"), field: "is_alcohol", type: "bool" },
    {
      key: "alcoholType", label: $_("pf.f.alcoholType"), field: "alcohol_type", type: "select",
      options: [{ value: "", label: "-" }, ...alcoholTypeOptions],
    },
    // The card's volume, read and typed in litres whatever unit it is kept in.
    { key: "litres", label: $_("pf.f.litres"), field: "volume_value", type: "dec" },
    // Picked from Настройки → Склад → Акцизный товар (2026-09-29); a name a
    // delivery note typed that matches no row there shows as it was typed.
    {
      key: "exciseStampSupplier", label: $_("pf.f.exciseStampSupplier"), field: "stamp_supplier_id", type: "select",
      options: [
        { value: "", label: "-" },
        ...stampSuppliersRef.map((s) => ({ value: String(s.id), label: s.name })),
      ],
    },
    { key: "exciseStampRange", label: $_("pf.f.exciseStampRange"), field: "excise_stamp_range", type: "text" },
    { key: "isCoffee", label: $_("pf.f.isCoffee"), field: "is_coffee", type: "bool" },
    // Logistics · single unit
    { key: "netWeight", label: $_("pf.f.netWeight"), field: "net_weight_kg", type: "dec", mandatory: true },
    { key: "grossWeight", label: $_("pf.f.grossWeight"), field: "gross_weight_kg", type: "dec", mandatory: true },
    { key: "length", label: `${unitSub} · ${$_("pf.f.length")}`, field: "length_cm", type: "dec" },
    { key: "width", label: `${unitSub} · ${$_("pf.f.width")}`, field: "width_cm", type: "dec" },
    { key: "height", label: `${unitSub} · ${$_("pf.f.height")}`, field: "height_cm", type: "dec" },
    // Logistics · display box
    { key: "dbLength", label: `${dispBox} · ${$_("pf.f.length")}`, field: "display_box_length_cm", type: "dec" },
    { key: "dbWidth", label: `${dispBox} · ${$_("pf.f.width")}`, field: "display_box_width_cm", type: "dec" },
    { key: "dbHeight", label: `${dispBox} · ${$_("pf.f.height")}`, field: "display_box_height_cm", type: "dec" },
    { key: "pcsPerDb", label: $_("pf.f.pcsPerDb"), field: "pcs_per_display_box", type: "num" },
    { key: "dbPerBox", label: $_("pf.f.dbPerBox"), field: "display_boxes_per_box", type: "num" },
    // Logistics · transport box
    { key: "boxLength", label: `${transBox} · ${$_("pf.f.length")}`, field: "box_length_cm", type: "dec" },
    { key: "boxWidth", label: `${transBox} · ${$_("pf.f.width")}`, field: "box_width_cm", type: "dec" },
    { key: "boxHeight", label: `${transBox} · ${$_("pf.f.height")}`, field: "box_height_cm", type: "dec" },
    { key: "pcsPerBox", label: $_("pf.f.pcsPerBox"), field: "pcs_per_box", type: "num" },
    { key: "boxesPerLayer", label: $_("pf.f.boxesPerLayer"), field: "boxes_per_layer", type: "num" },
    // Logistics · pallet
    { key: "palletType", label: $_("pf.f.palletType"), field: "pallet_type_id", type: "pallet" },
    { key: "palletAllowedHeight", label: $_("pf.f.palletAllowedHeight"), field: "pallet_allowed_height_m", type: "dec" },
    // Picked from the company's pallet list (Настройки → Склад → Паллеты),
    // and set by the pallet type; a weight typed on the card before stays.
    {
      key: "palletWeight", label: $_("pf.f.palletWeight"), field: "pallet_weight_kg", type: "select",
      options: [{ value: "", label: "-" }, ...palletWeightOptions(palletTypesRef)],
    },
    { key: "boxesPerPallet", label: $_("pf.f.boxesPerPallet"), field: "boxes_per_pallet", type: "ro" },
    { key: "layersPerPallet", label: $_("pf.f.layersPerPallet"), field: "layers_per_pallet", type: "num" },
    { key: "pcsPerPallet", label: $_("pf.f.pcsPerPallet"), field: "pcs_per_pallet", type: "ro" },
    { key: "totalGross", label: $_("pf.f.totalGross"), field: "total_pallet_gross_kg", type: "ro" },
    { key: "cargoNature", label: $_("pf.f.cargoNature"), field: "cargo_nature", type: "text" },
  ]);
  /* The picker's own running order: card sections, in card order. It is
     deliberately independent of the table's column order — see the note at the
     top of ColumnSettings. Anything not filed in a section trails at the end so
     a newly added column can never go missing from the picker. */
  const COLUMNS = $derived.by<ColumnDef[]>(() => {
    const byKey = new Map(COLS.map((c) => [c.key, c]));
    const out: ColumnDef[] = [];
    const seen = new Set<string>();
    for (const g of CARD_GROUPS) {
      for (const k of g.keys) {
        const c = byKey.get(k);
        // A mark section may repeat a column filed elsewhere (its «Объём»).
        if (!c || (seen.has(k) && !g.mark)) continue;
        seen.add(k);
        out.push({ key: c.key, label: c.label, locked: c.locked, group: g.label });
      }
    }
    for (const c of COLS) {
      if (seen.has(c.key) || ALL_FIELDS_ONLY.has(c.key) || MARK_ONLY_KEYS.has(c.key) || SHARED_KEYS.has(c.key)) continue;
      out.push({ key: c.key, label: c.label, locked: c.locked, group: GROUP_OF.get(c.key) });
    }
    return out;
  });

  // Plain key lists (no i18n) for visibility defaults + presets — keep in sync with COLS.
  const COL_KEYS = [
    "image","name","sku","ean","group","subgroup","manufacturer","supplier","brand","brandOwnership",
    "country","customs","hasExport","incoterms","partnerNote","description","composition","allergens","unit",
    "volumeUnit","volume","minOrder",
    "priceEur","vat","pallet","stock","expiresOn",
    "kcal","kj","fat","saturatedFat","carbs","sugars","protein","salt",
    "egais","alcoholPct","aceticAcidPct","isSupplement","standards","labelIcons",
    "bicarbonates","sulfates","chlorides","calcium","magnesium","sodium","potassium","vitaminC","vitaminD",
    "shelfLifeDays","minRemainingShelfLife","tempRegime","humidityMax","afterOpeningConditions",
    "afterOpeningHours","needsTempControl","storageConditions","leadTime",
    "languages","deposit","sugarExcise",
    "isAlcohol","alcoholType","litres","exciseStampSupplier","exciseStampRange",
    "isCoffee",
    "netWeight","grossWeight","length","width","height",
    "dbLength","dbWidth","dbHeight","pcsPerDb","dbPerBox",
    "boxLength","boxWidth","boxHeight","pcsPerBox","boxesPerLayer",
    "palletType","palletAllowedHeight","palletWeight","boxesPerPallet","layersPerPallet","pcsPerPallet","totalGross",
    "cargoNature",
  ];
  // Columns shown on first load, and the fallback "Selected" set before the user
  // has customised anything. The codes/group/brand/country/customs/export columns
  // are hidden by default to cut horizontal clutter — they stay re-enableable via
  // the "Screen layout settings" panel and the All-fields preset.
  const DEFAULT_VISIBLE_KEYS = ["image","name","supplier","unit","netWeight","grossWeight","stock"];
  /* Net and gross weight are stored in kg but shown in the unit each product is
     sold by, exactly as the product card does — so a 130 g sweet reads "130 g"
     and not "130 kg". They are therefore absent from SUFFIX and handled by
     `weightScaleForUnit` in `cellText` / the inline editor below. */
  const WEIGHT_KEYS = new Set(["netWeight", "grossWeight"]);
  /** Switched on by themselves once the company names its alcohol types. */
  const ALCOHOL_AUTO_KEYS = ["alcoholType", "litres", "exciseStampSupplier", "exciseStampRange"];

  // Per-column display unit suffixes (read-only formatting only).
  const SUFFIX: Record<string, string> = {
    length: " cm", width: " cm", height: " cm",
    fat: " g", saturatedFat: " g", carbs: " g", sugars: " g", protein: " g", salt: " g",
    alcoholPct: " %", aceticAcidPct: " %", vat: " %", minRemainingShelfLife: " %", humidityMax: " %",
    bicarbonates: " mg/l", sulfates: " mg/l", chlorides: " mg/l", calcium: " mg/l",
    magnesium: " mg/l", sodium: " mg/l", potassium: " mg/l", vitaminC: " mg",
    afterOpeningHours: " h",
    dbLength: " cm", dbWidth: " cm", dbHeight: " cm",
    boxLength: " cm", boxWidth: " cm", boxHeight: " cm",
    palletAllowedHeight: " m", palletWeight: " kg", totalGross: " kg",
  };
  const COLS_STORE_KEY = "pl.columns.selected.v5";
  const COL_ORDER_STORE_KEY = "pl.columns.order.v1";

  function loadColOrder(): string[] {
    try {
      const stored = localStorage.getItem(COL_ORDER_STORE_KEY);
      if (stored) {
        const saved = (JSON.parse(stored) as string[]).filter((k) => COL_KEYS.includes(k));
        // Columns added since the order was saved keep their declared place.
        return [...saved, ...COL_KEYS.filter((k) => !saved.includes(k))];
      }
    } catch {
      /* ignore malformed */
    }
    return [...COL_KEYS];
  }

  function loadSelectedCols(): Record<string, boolean> {
    const allFalse = Object.fromEntries(COL_KEYS.map((k) => [k, false]));
    try {
      const stored = localStorage.getItem(COLS_STORE_KEY);
      // A selection saved before the picker lost its secondary sections may still tick them.
      if (stored) return withoutAllFieldsOnly({ ...allFalse, ...(JSON.parse(stored) as Record<string, boolean>) });
    } catch {
      /* ignore malformed */
    }
    // Default selection: a trimmed set (see DEFAULT_VISIBLE_KEYS) to avoid horizontal clutter.
    return { ...allFalse, ...Object.fromEntries(DEFAULT_VISIBLE_KEYS.map((k) => [k, true])) };
  }

  // `selectedCols` is the user's saved custom selection — what the "Selected" view
  // restores. `colVisible` is the live display state (may temporarily be "All fields").
  // Persisting them separately means toggling "All fields" never clobbers the selection.
  let selectedCols = $state<Record<string, boolean>>(loadSelectedCols());
  // Intentionally seeded from the saved selection once — the two states then
  // evolve independently (see comment above).
  let colVisible = $state<Record<string, boolean>>(untrack(() => ({ ...selectedCols })));
  /* Left-to-right column order, chosen in the layout panel. Independent of
     which columns are on: turning one back on returns it to its own place
     rather than to the end. */
  let colOrder = $state<string[]>(loadColOrder());
  const colRank = (key: string) => {
    const i = colOrder.indexOf(key);
    return i === -1 ? colOrder.length + COL_KEYS.indexOf(key) : i;
  };
  // Column widths the user dragged in the header, remembered in this browser.
  const colWidths = columnWidths("products");
  const ORDERED_COLS = $derived([...COLS].sort((a, b) => colRank(a.key) - colRank(b.key)));
  // The row notices span the whole row: the «На сайте» cell, the visible
  // columns and the actions cell.
  const tableCols = $derived(
    3 + (editing ? 1 : 0) + ORDERED_COLS.filter((c) => colOn(c.key)).length,
  );

  const isAllPreset = $derived(COL_KEYS.every((k) => colVisible[k]));
  const isSelectedActive = $derived(
    !isAllPreset && COL_KEYS.every((k) => colVisible[k] === selectedCols[k]),
  );
  function presetSelected() {
    colVisible = { ...selectedCols };
  }
  function presetAll() {
    colVisible = Object.fromEntries(COL_KEYS.map((k) => [k, true]));
  }
  /* A manual checkbox edit in the Screen-layout panel (re)defines the saved
     selection. Made under «Все поля», it leaves that view: the secondary
     columns are not the picker's to keep. */
  function onColumnToggle() {
    colVisible = withoutAllFieldsOnly(colVisible);
    selectedCols = { ...colVisible };
  }

  // --- Inline cell edit -----------------------------------------------------
  let editCell = $state<{ id: number; key: string } | null>(null);
  let editVal = $state<string | number | null>("");
  /** The ticks of the allergen / standard / label-icon popover being edited. */
  let editIds = $state<number[]>([]);
  let popAt = $state({ x: 0, y: 0 });

  function focusOnMount(node: HTMLInputElement | HTMLTextAreaElement) {
    node.focus();
    node.select();
  }
  function focusFirst(node: HTMLElement) {
    (node.querySelector("input") ?? node).focus();
  }

  const countryOptions = $derived(
    countriesRef
      .map((c) => ({ value: String(c.id), label: localizedCountryName(c.code, $locale ?? "en", c.name) }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  );

  function refsOf(col: Col): Ref[] {
    if (col.key === "allergens") return allergensRef;
    if (col.key === "standards") return standardsRef;
    if (col.key === "labelIcons") return iconsRef;
    return [];
  }
  /* Allergens are named off their EU code (A01–A14), as on the card; the
     stored name is the source's bilingual label and only a fallback. */
  function refName(col: Col, r: Ref): string {
    if (col.key !== "allergens") return r.name;
    const key = `pf.allergen.${r.code}`;
    const label = $_(key);
    return label === key ? r.name : label;
  }

  /** Whether this user can change this cell here and now. */
  function isEditable(col: Col): boolean {
    if (!editing || !canEditProducts || col.type === "ro") return false;
    if (col.type === "cat") return true;
    if (!col.field) return false;
    if (col.type === "multi") return refsOf(col).length > 0;
    if (col.type === "country") return countriesRef.length > 0;
    if (col.type === "pallet") return palletTypesRef.length > 0;
    return true;
  }

  /** Where the popover opens: under the cell, or above it near the bottom. */
  function placePopover(el?: HTMLElement): { x: number; y: number } {
    const r = el?.getBoundingClientRect();
    if (!r) return { x: 16, y: 16 };
    const W = 300;
    const H = 340;
    return {
      x: Math.max(8, Math.min(r.left, window.innerWidth - W - 8)),
      y: r.bottom + 4 + H <= window.innerHeight ? r.bottom + 4 : Math.max(8, r.top - H - 4),
    };
  }

  function startEdit(p: ProductListItem, col: Col, el?: HTMLElement) {
    if (!isEditable(col) || col.type === "bool") return;
    if (col.type === "cat") {
      // Category cells carry no scalar field — the select reads the tree.
      editCell = { id: p.id, key: col.key };
      return;
    }
    const raw = (p as Record<string, unknown>)[col.field!];
    editCell = { id: p.id, key: col.key };
    if (col.type === "multi") {
      editIds = [...((raw as number[] | undefined) ?? [])];
      popAt = placePopover(el);
    } else if (col.type === "langs") {
      editVal = ((raw as string[] | undefined) ?? []).join(", ");
    } else if (col.type === "country" || col.type === "pallet") {
      editVal = raw == null ? "" : String(raw);
    }
    // Show barcodes as plain digits (never the imported "4.74489E+12" exponent).
    else if (col.key === "ean") editVal = formatBarcode(raw as string);
    else if (col.key === "litres") editVal = litresOf(p) ?? "";
    else if (col.key === "exciseStampSupplier") editVal = raw == null ? "" : String(raw);
    // Weights are edited in the product's own unit, matching what the cell shows.
    else if (WEIGHT_KEYS.has(col.key)) editVal = scaleToDisplay(raw, weightScaleForUnit(p.unit));
    else editVal = (raw as string | number | null) ?? "";
  }
  function cancelEdit() {
    editCell = null;
  }
  function toggleId(id: number, on: boolean) {
    editIds = on ? [...editIds, id] : editIds.filter((x) => x !== id);
  }
  /** «ee, lv;LT» → ["EE", "LV", "LT"]: two-letter codes, each once. */
  function parseLangs(raw: string): string[] {
    const codes = raw.split(/[\s,;]+/).map((s) => s.trim().toUpperCase()).filter((s) => /^[A-Z]{2}$/.test(s));
    return [...new Set(codes)];
  }

  async function commitEdit(p: ProductListItem, col: Col) {
    if (!col.field || !editCell) return;
    if (col.key === "litres") {
      editCell = null;
      await saveLitres(p, editVal);
      return;
    }
    let val: unknown = editVal;
    if (col.type === "num") val = editVal === "" || editVal === null ? null : parseInt(String(editVal), 10);
    // Typed in the product's unit → back to the kilograms the column stores.
    else if (WEIGHT_KEYS.has(col.key)) val = scaleToStored(editVal ?? "", weightScaleForUnit(p.unit));
    else if (col.type === "dec") val = editVal === "" || editVal === null ? null : Number(editVal);
    else if (col.type === "country" || col.type === "pallet") val = editVal ? Number(editVal) : null;
    else if (col.key === "exciseStampSupplier") val = editVal ? Number(editVal) : null;
    else if (col.key === "palletWeight") val = editVal ? String(editVal) : null;
    else if (col.type === "langs") val = parseLangs(String(editVal ?? ""));
    else if (col.type === "multi") val = [...editIds];
    editCell = null;
    const blank = val === null || (typeof val === "number" && isNaN(val)) || (typeof val === "string" && !val.trim());
    // A name, a price or a lead time the database cannot hold empty.
    if (col.required && blank) return;
    await saveField(p, col.field, val);
  }

  const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

  /* One field, saved at once. The row shows the new value straight away and
     then takes the server's copy of the whole card: a pallet type fills in the
     pallet figures, a country and a pallet type come back with their names. */
  async function saveField(p: ProductListItem, field: string, val: unknown) {
    const rec = p as Record<string, unknown>;
    const prev = rec[field];
    if (same(val, prev)) return;
    rec[field] = val; // optimistic
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fresh = await updateProduct(slug, p.id, { [field]: val } as any);
      absorb(p, fresh as unknown as Record<string, unknown>);
    } catch {
      rec[field] = prev; // revert
      showToast($_("pl.toggle.error"));
    }
  }
  /** The card's volume in litres — it may be kept in millilitres. */
  function litresOf(p: ProductListItem): number | null {
    if (p.volume_value == null || (p.volume_value as unknown) === "") return null;
    const v = Number(p.volume_value);
    return p.volume_unit === "ml" ? v / 1000 : v;
  }

  /* «Литраж» is typed in litres and kept in the card's own unit: a card on
     millilitres stays on them, anything else becomes litres. */
  async function saveLitres(p: ProductListItem, raw: unknown) {
    const text = String(raw ?? "").trim().replace(",", ".");
    const litres = text === "" ? null : Number(text);
    if (litres !== null && (isNaN(litres) || litres < 0)) return;
    if (litres === litresOf(p)) return;
    const patch: Record<string, unknown> =
      litres === null
        ? { volume_value: null }
        : p.volume_unit === "ml"
          ? { volume_value: Math.round(litres * 1000 * 1000) / 1000 }
          : { volume_unit: "l", volume_value: litres };
    const prev = { unit: p.volume_unit, value: p.volume_value };
    Object.assign(p, patch);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      absorb(p, (await updateProduct(slug, p.id, patch as any)) as unknown as Record<string, unknown>);
    } catch {
      p.volume_unit = prev.unit;
      p.volume_value = prev.value;
      showToast($_("pl.toggle.error"));
    }
  }

  function absorb(p: ProductListItem, fresh: Record<string, unknown>) {
    const rec = p as Record<string, unknown>;
    for (const key of Object.keys(rec)) {
      if (key in fresh && key !== "canonical_partner_note") rec[key] = fresh[key];
    }
    const ids = (k: string) => ((fresh[k] as { id: number }[] | undefined) ?? []).map((x) => x.id);
    if ("allergens" in fresh) p.allergen_ids = ids("allergens");
    if ("standards" in fresh) p.standard_ids = ids("standards");
    if ("label_icons" in fresh) p.label_icon_ids = ids("label_icons");
  }

  /* ── Inline category edit (group / subgroup dropdowns) ────────────────
     A product stores the leaf: the subcategory when one is chosen, else the
     top-level group. The two cells edit the same field from both ends. */
  function groupIdOf(p: ProductListItem): number | null {
    const cat = categories.find((c) => c.id === p.category_id);
    return cat ? (cat.parent_id ?? cat.id) : null;
  }
  function subIdOf(p: ProductListItem): number | null {
    const cat = categories.find((c) => c.id === p.category_id);
    return cat && cat.parent_id != null ? cat.id : null;
  }
  async function commitCategory(p: ProductListItem, groupId: number | null, subId: number | null) {
    editCell = null;
    const newId = subId ?? groupId ?? null;
    if (newId === p.category_id) return;
    const prev = { id: p.category_id, g: p.group_name, s: p.subgroup_name };
    const group = groupId != null ? categories.find((c) => c.id === groupId) : undefined;
    const sub = subId != null ? categories.find((c) => c.id === subId) : undefined;
    p.category_id = newId;
    p.group_name = group?.name ?? null;
    p.subgroup_name = sub?.name ?? null;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await updateProduct(slug, p.id, { category_id: newId } as any);
    } catch {
      p.category_id = prev.id;
      p.group_name = prev.g;
      p.subgroup_name = prev.s;
      showToast($_("pl.toggle.error"));
    }
  }
  function focusSelect(node: HTMLSelectElement) {
    node.focus();
  }

  function cellText(p: ProductListItem, col: Col): string {
    switch (col.key) {
      case "ean": return formatBarcode(p.ean) || "-";
      case "group": return p.group_name || "-";
      case "subgroup": return p.subgroup_name || "-";
      case "country": return localizedCountryName(p.origin_country_code ?? "", $locale ?? "en", p.origin_country_name ?? "") || p.origin_country_code || "-";
      case "unit": return p.unit || "-";
      case "priceEur": return Number(p.price_eur).toFixed(priceDecimals(slug));
      case "palletType": return p.pallet_type_name || p.pallet_type_code || "-";
      case "languages": return p.packaging_languages?.length ? p.packaging_languages.join(", ") : "-";
      case "palletWeight":
        return p.pallet_weight_kg == null ? "-" : `${Number(p.pallet_weight_kg)} kg`;
      case "expiresOn":
        return p.nearest_expires_on ? formatDate(p.nearest_expires_on) : "-";
      case "volume":
        return p.volume_value == null ? "-" : `${Number(p.volume_value)}${p.volume_unit ? ` ${p.volume_unit}` : ""}`;
      case "litres": {
        const l = litresOf(p);
        return l == null ? "-" : `${l.toLocaleString($locale ?? "en", { maximumFractionDigits: 3 })} l`;
      }
      case "exciseStampSupplier":
        return (
          stampSuppliersRef.find((s) => s.id === p.stamp_supplier_id)?.name || p.excise_stamp_supplier || "-"
        );
      case "alcoholType":
        return p.alcohol_type
          ? alcoholTypeName(alcoholTypesRef.find((t) => t.code === p.alcohol_type), $locale) || p.alcohol_type
          : "-";
    }
    const v = col.field ? (p as Record<string, unknown>)[col.field] : null;
    if (col.type === "bool") return v ? $_("pf.yes") : $_("pf.no");
    if (col.type === "multi") {
      const ids = (v as number[] | undefined) ?? [];
      if (ids.length === 0) return "-";
      const refs = refsOf(col);
      // Before the reference list is in, the count is all that can be said.
      if (refs.length === 0) return String(ids.length);
      return refs.filter((r) => ids.includes(r.id)).map((r) => refName(col, r)).join(", ") || "-";
    }
    if (col.type === "select") {
      if (v === null || v === undefined || v === "") return "-";
      return col.options?.find((o) => o.value === v)?.label ?? String(v);
    }
    if (v === null || v === undefined || v === "") return "-";
    if (WEIGHT_KEYS.has(col.key)) {
      const s = weightScaleForUnit(p.unit);
      return `${scaleToDisplay(v, s)} ${s.unit}`;
    }
    return `${v}${SUFFIX[col.key] ?? ""}`;
  }

  $effect(() => {
    // Persist the saved selection (not the transient "All fields" view).
    localStorage.setItem(COLS_STORE_KEY, JSON.stringify(selectedCols));
  });

  $effect(() => {
    localStorage.setItem(COL_ORDER_STORE_KEY, JSON.stringify(colOrder));
  });

  async function load() {
    loading = true;
    try {
      const p: Record<string, string | number | boolean | undefined> = {
        // The server's order is the name; a column header re-sorts in place.
        order_by: "name",
        // One scrolling list, no pages (owner, 2026-09-16).
        all: true,
        active: status === "archived" ? false : status !== "all" || undefined,
        include_archived: status === "all" || undefined,
      };
      // Sub-category narrows further than category; otherwise the category
      // includes its sub-groups (handled server-side).
      if (selectedSubcategory) p.category_id = selectedSubcategory;
      else if (selectedCategory) p.category_id = selectedCategory;
      if (status === "in_stock") p.in_stock = true;
      if (mark) p.mark = mark;
      if (searchQ) p.q = searchQ;
      if (selectedSupplier) p.supplier = selectedSupplier;
      if (gapFilter) p.gap = gapFilter;

      const res = await fetchProducts(slug, p);
      products = res.results;
      total = res.count;
      loadError = false;
    } catch (e) {
      console.error("ProductList load error:", e);
      loadError = true;
    } finally {
      loading = false;
    }
  }

  async function loadRefs() {
    const [countries, pallets, allergens, standards, icons] = await Promise.all([
      fetchCountries().catch(() => [] as CountryRef[]),
      fetchPalletTypes(slug).catch(() => [] as PalletTypeRef[]),
      fetchAllergens().catch(() => [] as Ref[]),
      fetchProductStandards().catch(() => [] as Ref[]),
      fetchLabelIcons().catch(() => [] as Ref[]),
    ]);
    countriesRef = countries;
    palletTypesRef = pallets;
    allergensRef = allergens;
    standardsRef = standards;
    iconsRef = icons;
  }

  async function init() {
    loadError = false;
    loading = true;
    try {
      let onCards: string[];
      [categories, onCards, dirSuppliers] = await Promise.all([
        fetchCategories(slug),
        fetchProductSuppliers(slug).catch(() => []),
        fetchSupplierNames(slug),
      ]);
      const partners = new Set(dirSuppliers.map((n) => n.toLowerCase()));
      suppliers = [...dirSuppliers, ...onCards.filter((n) => !partners.has(n.toLowerCase()))].sort((a, b) =>
        a.localeCompare(b),
      );
      // A remembered supplier that is neither a partner nor on any card would
      // list nothing — but one asked for by name is kept: its empty list
      // offers the ways in.
      if (selectedSupplier && !suppliers.includes(selectedSupplier)) {
        if (selectedSupplier === qsSupplier) suppliers = [...suppliers, qsSupplier].sort();
        else selectedSupplier = "";
      }
      // Applied once: a reload keeps whatever is picked from here on.
      if (qsSupplier) replace(`/t/${slug}/products`);
      // Nor would a remembered category the tree no longer has.
      const known = knownCategories(categories, selectedCategory, selectedSubcategory);
      selectedCategory = known.category;
      selectedSubcategory = known.subcategory;
      await load();
    } catch (e) {
      console.error("ProductList init error:", e);
      loadError = true;
      loading = false;
    }
    loadRefs();
    // Non-fatal: the badge just stays at 0 when the endpoint is unavailable
    // (e.g. company types without a supplier directory).
    await loadSupplierReview();
  }

  onMount(init);
  onMount(() => {
    fetchProductMarks(slug).then((m) => (customMarks = m)).catch(() => {});
    fetchStockSettings(slug)
      .then((st) => {
        keptMarks = st.product_marks ?? [];
        alcoholTicked = st.alcohol_types ?? [];
      })
      .catch(() => {});
    fetchAlcoholTypes().then((t) => (alcoholTypesRef = t)).catch(() => {});
    fetchStampSuppliers(slug).then((r) => (stampSuppliersRef = r)).catch(() => {});
  });

  /* «Вид алкоголя» appears by itself (owner, 2026-09-29): once a type is
     ticked in Настройки → Склад → Акцизный товар and a card carries the
     alcohol mark, the type, litrage and excise stamp columns are switched on
     — once per company in this browser, so a column the user then turns off
     stays off. */
  $effect(() => {
    if (!alcoholTicked?.length || keptMarks === null || !markKept("alcohol")) return;
    const onScreen = products.some((p) => p.is_alcohol);
    untrack(() => seedAlcoholColumns(onScreen));
  });
  let alcoholProbed = false;
  function seedAlcoholColumns(onScreen: boolean) {
    const key = `pl.columns.alcoholAuto.v1:${slug}`;
    try {
      if (localStorage.getItem(key)) return;
    } catch {
      return;
    }
    const switchOn = () => {
      try {
        localStorage.setItem(key, "1");
      } catch {
        /* the columns still show this once */
      }
      const on = Object.fromEntries(ALCOHOL_AUTO_KEYS.map((k) => [k, true]));
      selectedCols = { ...selectedCols, ...on };
      // Side by side, in the order the owner named them.
      const at = Math.min(...ALCOHOL_AUTO_KEYS.map((k) => colOrder.indexOf(k)).filter((i) => i >= 0));
      const rest = colOrder.filter((k) => !ALCOHOL_AUTO_KEYS.includes(k));
      const pos = Number.isFinite(at) ? colOrder.slice(0, at).filter((k) => !ALCOHOL_AUTO_KEYS.includes(k)).length : rest.length;
      colOrder = [...rest.slice(0, pos), ...ALCOHOL_AUTO_KEYS, ...rest.slice(pos)];
      if (!isAllPreset) colVisible = { ...colVisible, ...on };
    };
    if (onScreen) return switchOn();
    // The list may be filtered: ask whether any card carries the mark.
    if (alcoholProbed) return;
    alcoholProbed = true;
    fetchProducts(slug, { mark: "alcohol", include_archived: true })
      .then((res) => res.count && switchOn())
      .catch(() => {});
  }

  /* The «Обновления от поставщиков» badge counts card edits and new positions;
     the edits also feed the strip under each changed row. */
  async function loadSupplierReview() {
    const [upd, offers] = await Promise.all([
      fetchPendingSupplierUpdates(slug).catch(() => [] as ProductUpdates[]),
      fetchSupplierOffers(slug).catch(() => []),
    ]);
    pendingUpdates = upd;
    updatesCount = upd.length + offers.length;
  }

  // ?gap= in the address (dashboard hint, e-mail link) narrows the list.
  $effect(() => {
    const g = qsGap;
    untrack(() => {
      const next = g === "pending" || g === "any" || g === "back" ? g : "";
      if (next === gapFilter) return;
      gapFilter = next;
      view = "list";
      page = 1;
      if (!loading) load();
    });
  });

  function gapText(p: ProductListItem): string {
    const values = {
      supplier: p.supplier || "-",
      date: p.supplier_gap_since ? formatDate(p.supplier_gap_since) : "-",
    };
    const reason =
      p.supplier_gap_reason === "delisted"
        ? $_("gap.reason.delisted", { values })
        : p.supplier_gap_reason === "link_ended"
          ? $_("gap.reason.link_ended", { values })
          : $_("gap.reason.access_closed", { values });
    return `${reason}. ${$_("gap.row.ask")}`;
  }

  function changedFields(u: ProductUpdates): string {
    const names = u.changes.map((c) => productFieldLabel(c.field, (k) => $_(k)));
    return names.length > 4 ? `${names.slice(0, 4).join(", ")} +${names.length - 4}` : names.join(", ");
  }

  async function resolveRowUpdates(p: ProductListItem, action: "accept" | "reject") {
    rowBusy = p.id;
    try {
      await resolveAllSupplierUpdates(slug, p.id, action);
      pendingUpdates = pendingUpdates.filter((u) => u.product_id !== p.id);
      updatesCount = Math.max(0, updatesCount - 1);
      if (action === "accept") await load();
    } catch (e) {
      console.error("resolve supplier updates failed", e);
    } finally {
      rowBusy = null;
    }
  }

  async function resolveBack(p: ProductListItem, restore: boolean) {
    rowBusy = p.id;
    try {
      await resolveSupplierBack(slug, [p.id], restore);
      await load();
    } catch (e) {
      console.error("resolve supplier back failed", e);
    } finally {
      rowBusy = null;
    }
  }

  function pendingGapIds(): number[] {
    return products
      .filter((p) => p.supplier_gap_reason && !p.supplier_gap_decided_at && p.is_active)
      .map((p) => p.id);
  }

  function filterChange() {
    page = 1;
    load();
  }

  /* The shared row: a search reloads once typing pauses, a picker at once. */
  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  function onFilter(what: FilterChange) {
    page = 1;
    if (searchTimer) clearTimeout(searchTimer);
    if (what === "q") searchTimer = setTimeout(load, 250);
    else load();
  }

  function clearFilters() {
    searchQ = "";
    selectedCategory = null;
    selectedSubcategory = null;
    selectedSupplier = "";
    mark = "";
    status = "active";
    filterChange();
  }

  function goProduct(id: number) {
    push(`/t/${slug}/products/${id}`);
  }

  /* «В архив» moved here from Склад (owner, 2026-09-19). The same rule: only
     an article with nothing left on the shelf goes to the archive, and
     unticking it (under «Все») brings it back. */
  const canArchiveAny = $derived(canAction($auth, slug, "stock.settings", ["products", "edit"]));
  const canArchive = (p: ProductListItem) => !p.is_active || Number(p.total_stock ?? 0) <= 0;
  async function toggleArchive(p: ProductListItem, archived: boolean) {
    try {
      await archiveProduct(slug, p.id, archived);
    } catch (err: any) {
      showToast(err?.message ?? $_("common.actionFailed"));
    }
    await load();
  }

  /* A column header sorts the list — the whole list is on the page, so the
     sort runs here, over what the cell shows: numbers as numbers, the rest
     alphabetically, blanks last whichever way. */
  let sortKey = $state("");
  let sortDir = $state<"asc" | "desc">("asc");
  function sortBy(key: string) {
    if (sortKey === key) sortDir = sortDir === "asc" ? "desc" : "asc";
    else { sortKey = key; sortDir = "asc"; }
  }
  const sortIcon = (key: string) =>
    sortKey !== key ? "unfold_more" : sortDir === "asc" ? "arrow_upward" : "arrow_downward";
  function sortValue(p: ProductListItem, col: Col): string | number | null {
    if (col.key === "stock") return Number(p.total_stock ?? 0);
    if (col.key === "group") return p.group_name || null;
    if (col.key === "subgroup") return p.subgroup_name || null;
    // ISO dates sort as written; the shown dd/mm/yyyy would not.
    if (col.key === "expiresOn") return p.nearest_expires_on || null;
    if (col.key === "palletWeight") return p.pallet_weight_kg == null ? null : Number(p.pallet_weight_kg);
    const v = col.field ? (p as Record<string, unknown>)[col.field] : null;
    if (v === null || v === undefined || v === "") return null;
    if (col.type === "num" || col.type === "dec" || col.key === "priceEur" || WEIGHT_KEYS.has(col.key)) {
      const n = Number(v);
      return isNaN(n) ? null : n;
    }
    if (col.type === "bool") return v ? 1 : 0;
    const t = cellText(p, col);
    return t === "-" ? null : t.toLocaleLowerCase();
  }
  const sortedProducts = $derived.by(() => {
    const col = COLS.find((c) => c.key === sortKey);
    if (!col) return products;
    const sign = sortDir === "asc" ? 1 : -1;
    return [...products].sort((a, b) => {
      const va = sortValue(a, col);
      const vb = sortValue(b, col);
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * sign;
      return String(va).localeCompare(String(vb)) * sign;
    });
  });

  /* Two ticks open every row (owner, 2026-09-16):

     «На сайте» — the card is on the company's public page, to tell a
     prospective client the goods can be supplied. It writes `public_visible`,
     which is what the public page reads (with the card not archived). The
     archive — `is_active` — is Склад's «В архив» and no longer rides on this
     tick: hiding a card from strangers must not take it off clients' order
     sheets.

     «К заказу» — `is_orderable`, switched off while the goods are
     temporarily unavailable: clients still see the card, greyed, but cannot
     order it, and its row is greyed on «Цены и доступ». */
  const onSite = (p: ProductListItem) => p.is_active && p.public_visible;

  /* The tick flips itself and the state follows it (2026-09-22). It used to
     preventDefault the click and set the state inside the handler: Svelte
     wrote the new value, then the browser undid the click on top of it, so
     the tick showed the old state until a reload. A refused toggle puts the
     tick back by hand. */
  function untick(e: Event, value: boolean) {
    (e.currentTarget as HTMLInputElement).checked = value;
  }

  async function toggleVisibility(p: ProductListItem, e: Event) {
    e.stopPropagation();
    if (togglingId !== null) return untick(e, onSite(p));
    const next = !p.public_visible;
    // Publishing is a seller feature; hiding never is.
    if (next && !hasEntitlement($auth, slug, "publish_catalog")) {
      untick(e, onSite(p));
      requestUpgrade("publish_catalog");
      return;
    }
    p.public_visible = next;
    togglingId = p.id;
    try {
      await updateProduct(slug, p.id, { public_visible: next });
    } catch {
      p.public_visible = !next;
      showToast($_("pl.toggle.error"));
    } finally {
      togglingId = null;
    }
  }

  async function toggleOrderable(p: ProductListItem, e: Event) {
    e.stopPropagation();
    if (togglingId !== null) return untick(e, p.is_orderable ?? true);
    const next = !(p.is_orderable ?? true);
    p.is_orderable = next;
    togglingId = p.id;
    try {
      await updateProduct(slug, p.id, { is_orderable: next });
    } catch {
      p.is_orderable = !next;
      showToast($_("pl.toggle.error"));
    } finally {
      togglingId = null;
    }
  }

  /* Header ticks (owner, 2026-09-23): pick a supplier, then one click ticks
     «На сайте» or «К заказу» on every row the list shows — or clears them
     all when every one is already ticked. Archived rows cannot go on the
     site, so the site tick counts only the active ones. */
  let bulkBusy = $state(false);
  const siteRows = $derived(sortedProducts.filter((p) => p.is_active));
  const allOnSite = $derived(siteRows.length > 0 && siteRows.every(onSite));
  const someOnSite = $derived(!allOnSite && siteRows.some(onSite));
  const allOrderable = $derived(
    sortedProducts.length > 0 && sortedProducts.every((p) => p.is_orderable ?? true),
  );
  const someOrderable = $derived(
    !allOrderable && sortedProducts.some((p) => p.is_orderable ?? true),
  );
  function indeterminate(node: HTMLInputElement, value: boolean) {
    node.indeterminate = value;
    return { update: (v: boolean) => (node.indeterminate = v) };
  }

  async function tickAll(field: "public_visible" | "is_orderable", e: Event) {
    const rows = field === "public_visible" ? siteRows : sortedProducts;
    const all = field === "public_visible" ? allOnSite : allOrderable;
    if (bulkBusy || rows.length === 0) return untick(e, all);
    const next = !all;
    if (field === "public_visible" && next && !hasEntitlement($auth, slug, "publish_catalog")) {
      untick(e, all);
      requestUpgrade("publish_catalog");
      return;
    }
    const prev = rows.map((p) => [p, p[field]] as const);
    for (const p of rows) p[field] = next;
    bulkBusy = true;
    try {
      await setProductFlags(slug, rows.map((p) => p.id), { [field]: next });
    } catch {
      for (const [p, v] of prev) p[field] = v;
      showToast($_("pl.toggle.error"));
    } finally {
      bulkBusy = false;
    }
  }

  function showToast(msg: string) {
    toast = msg;
    setTimeout(() => (toast = ""), 3000);
  }

  // ── Batch photo upload (file name = EAN, e.g. 4848484848489-1.jpg) ───────
  let photoInputEl = $state<HTMLInputElement | null>(null);
  let uploadingPhotos = $state(false);
  let uploadProgress = $state<{ done: number; total: number } | null>(null);
  /** Per-file outcome of the last batch. A toast can say "3 skipped"; only a
   *  list can say WHICH three and why, and with 200 files that difference is
   *  the whole feature. */
  let bulkResult = $state<BulkImageResult | null>(null);

  async function handlePhotoFiles(files: File[]) {
    if (files.length === 0 || uploadingPhotos) return;
    uploadingPhotos = true;
    uploadProgress = { done: 0, total: files.length };
    try {
      const res = await bulkUploadProductImagesBatched(slug, files, (done, total) => {
        uploadProgress = { done, total };
      });
      if (res.skipped.length > 0) {
        bulkResult = res;
      } else {
        showToast(
          $_("pl.bulkPhotos.result", {
            values: { attached: res.attached.length, skipped: 0 },
          }),
        );
      }
      if (res.attached.length > 0) await load();
    } catch (err: any) {
      showToast(err?.message ?? $_("common.actionFailed"));
    } finally {
      uploadingPhotos = false;
      uploadProgress = null;
    }
  }

  async function onBulkPhotos(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = "";
    await handlePhotoFiles(files);
  }

  /* Whole-page drop target: a folder of EAN-named photos dragged from the
     desktop lands anywhere on the list. The counter survives child
     enter/leave churn; only drags that actually carry files count. */
  let dragDepth = $state(0);
  const dragHasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");

  function onPageDragEnter(e: DragEvent) {
    if (!canEditProducts || !dragHasFiles(e)) return;
    e.preventDefault();
    dragDepth += 1;
  }
  function onPageDragOver(e: DragEvent) {
    if (!canEditProducts || !dragHasFiles(e)) return;
    e.preventDefault();
  }
  function onPageDragLeave(e: DragEvent) {
    if (dragDepth === 0 || !dragHasFiles(e)) return;
    dragDepth -= 1;
  }
  function onPageDrop(e: DragEvent) {
    if (!canEditProducts || !dragHasFiles(e)) return;
    e.preventDefault();
    dragDepth = 0;
    const files = Array.from(e.dataTransfer?.files ?? []).filter(
      (f) => f.type.startsWith("image/") || /\.(jpe?g|png|webp|gif)$/i.test(f.name),
    );
    handlePhotoFiles(files);
  }

  function skipReasonLabel(reason: string): string {
    const key = `pl.bulkPhotos.reason.${reason}`;
    const label = $_(key);
    return label === key ? reason : label;
  }

</script>

<AppShell tenantSlug={slug} active="stock">
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    class="pl-page"
    ondragenter={onPageDragEnter}
    ondragover={onPageDragOver}
    ondragleave={onPageDragLeave}
    ondrop={onPageDrop}
  >
    {#if dragDepth > 0}
      <div class="drop-overlay">
        <div class="drop-overlay-inner">
          <span class="material-icons-outlined drop-icon">add_photo_alternate</span>
          <p class="drop-title">{$_("pl.bulkPhotos.dropTitle")}</p>
          <p class="drop-hint">{$_("pl.bulkPhotos.hint")}</p>
        </div>
      </div>
    {/if}
    {#if uploadProgress && uploadProgress.total > 1}
      <div class="upload-progress">
        {$_("pl.bulkPhotos.progress", {
          values: { done: uploadProgress.done, total: uploadProgress.total },
        })}
      </div>
    {/if}
    <CatalogHead
      {slug}
      view="products"
      bind:mode
      modeHint={$_("catalog.mode.productsHint")}
      onreselect={() => (view = "list")}
    >
      {#snippet title()}
        <SectionGuide id="products" />
      {/snippet}
      {#snippet actions()}
        {#if view === "list"}
          <div class="header-actions">
            <button class="btn btn--secondary" onclick={() => push(`/t/${slug}/products/import${supplierQs}`)} disabled={!canEditProducts} title={!canEditProducts ? $_("permissions.readOnly") : undefined}>
              <span class="material-icons-outlined">upload_file</span>
              {$_("pl.toolbar.import")}
            </button>
            <!-- Batch photos: file names are EANs (4848484848489-1.jpg, -2, …),
                 each lands on the product with that barcode. -->
            <input
              bind:this={photoInputEl}
              type="file"
              accept="image/*"
              multiple
              class="visually-hidden-input"
              onchange={onBulkPhotos}
            />
            <button
              class="btn btn--secondary"
              onclick={() => photoInputEl?.click()}
              disabled={!canEditProducts || uploadingPhotos}
              title={!canEditProducts ? $_("permissions.readOnly") : $_("pl.bulkPhotos.hint")}
            >
              <span class="material-icons-outlined">add_photo_alternate</span>
              {uploadingPhotos ? $_("common.saving") : $_("pl.bulkPhotos.button")}
            </button>
            <button class="btn btn--primary" onclick={() => push(`/t/${slug}/products/new${supplierQs}`)} disabled={!canEditProducts} title={!canEditProducts ? $_("permissions.readOnly") : undefined}>
              <span class="material-icons-outlined">add</span>
              {$_("pl.empty.addLink")}
            </button>
          </div>
        {/if}
      {/snippet}
      <!-- The category tree and the review of supplier edits to linked cards,
           two buttons under the head (2026-09-19). A button pressed again, or
           «Продукты» above, returns to the list. Shops used to get neither;
           reversed 2026-08-23, because a shop owns its stock cards like anyone
           else and the cards it received from connected suppliers get edited
           upstream just the same. Since 2026-09-22 they are the head's `subnav`
           row, with the page buttons and «Редакция / Сводная» on its right. -->
      {#snippet subnav()}
        <div class="pl-tabs">
          {#if canViewCategories}
            <button type="button" class="pl-tab" class:active={view === "categories"} onclick={() => (view = view === "categories" ? "list" : "categories")}>
              <span class="material-icons-outlined" aria-hidden="true">account_tree</span>
              {$_("categories.title")}
            </button>
          {/if}
          <button type="button" class="pl-tab" class:active={view === "updates"} onclick={() => (view = view === "updates" ? "list" : "updates")}>
            <span class="material-icons-outlined" aria-hidden="true">sync</span>
            {$_("supdir.updates.title")}
            {#if updatesCount > 0}<span class="pl-tab-badge">{updatesCount}</span>{/if}
          </button>
        </div>
      {/snippet}
    </CatalogHead>
    {#if view !== "categories"}
      <SetupHint {slug} id="no_client_access" />
      {#if !gapFilter}<SetupHint {slug} id="supplier_unavailable" />{/if}
    {/if}

    {#if view === "categories"}
      <ProductCategories {slug} />
    {/if}

    {#if view === "list"}
      {#if gapFilter}
        <div class="gap-filter" role="status">
          <span class="material-icons-outlined" aria-hidden="true">remove_shopping_cart</span>
          <span class="gap-filter-text">
            {gapFilter === "back" ? $_("gap.filter.back") : $_("gap.filter.pending")}
          </span>
          {#if gapFilter === "pending" && pendingGapIds().length > 1 && canEditProducts}
            <button type="button" class="row-note-btn row-note-btn--primary" onclick={() => (gapDialogIds = pendingGapIds())}>
              {$_("gap.filter.decideAll")}
            </button>
          {/if}
          <button type="button" class="row-note-btn" onclick={() => push(`/t/${slug}/products`)}>
            {$_("gap.filter.clear")}
          </button>
        </div>
      {/if}
      <CatalogFilterBar
        bind:q={searchQ}
        bind:category={selectedCategory}
        bind:subcategory={selectedSubcategory}
        bind:supplier={selectedSupplier}
        {categories}
        {suppliers}
        oncolumns={layout === "table" ? () => (columnsOpen = true) : undefined}
        onchange={onFilter}
      >
        {#snippet options()}
          <div class="preset-switch" role="group" aria-label={$_("pl.layout.label")}>
            <button type="button" class:active={layout === "table"} onclick={() => (layout = "table")}>
              <span class="material-icons-outlined" aria-hidden="true">table_rows</span>
              {$_("pl.layout.table")}
            </button>
            <button type="button" class:active={layout === "catalog"} onclick={() => (layout = "catalog")}>
              <span class="material-icons-outlined" aria-hidden="true">grid_view</span>
              {$_("pl.layout.catalog")}
            </button>
          </div>
          {#if layout === "table"}
            <div class="preset-switch" role="group" aria-label={$_("pl.preset.label")}>
              <button type="button" class:active={isSelectedActive} onclick={presetSelected}>
                {$_("pl.preset.mandatory")}
              </button>
              <button type="button" class:active={isAllPreset} onclick={presetAll}>
                {$_("pl.preset.all")}
              </button>
            </div>
            <!-- How the cells edit, said where they are edited. -->
            <InfoTooltip text={$_("guide.products.how.7")} />
          {/if}
          <select class="opt-select" bind:value={status} onchange={filterChange} aria-label={$_("pl.col.status")}>
            <option value="all">{$_("common.all")}</option>
            <option value="active">{$_("pl.status.active")}</option>
            <option value="in_stock">{$_("availability.in_stock")}</option>
            <option value="archived">{$_("pl.status.archived")}</option>
          </select>
          <select class="opt-select" bind:value={mark} onchange={filterChange} aria-label={$_("stock.filter.mark")}>
            <option value="">{$_("stock.filter.markAll")}</option>
            {#each markOptions as m (m)}
              <option value={m}>{$_(`receipt.mark.${m}`)}</option>
            {/each}
            {#each customMarks as m (m.id)}
              <option value={`c${m.id}`}>{m.name}</option>
            {/each}
          </select>
        {/snippet}
      </CatalogFilterBar>
    {/if}

    {#if view === "categories"}
      <!-- The tree is rendered above; the list below belongs to the other tabs. -->
    {:else if view === "updates"}
      <SupplierUpdates {slug} oncount={(n) => (updatesCount = n)} />
    {:else if loading}
      <div class="loading-state">{$_("pl.loading")}...</div>
    {:else if loadError}
      <LoadErrorState onretry={init} />
    {:else if products.length === 0}
      {@const hasFilters = !!searchQ || !!selectedCategory || !!selectedSupplier || !!mark || status !== "active"}
      {@const onlyCategoryFilter = !!selectedCategory && !searchQ && !selectedSupplier && !mark && status === "active"}
      {#if onlyCategoryFilter}
        <!-- Empty category → actionable CTA that pre-selects the category in the
             create form, instead of a dead-end "no matches" message. -->
        <div class="empty-state card empty-state--onboarding">
          <span class="material-icons-outlined empty-icon">inventory_2</span>
          <p class="empty-title">{$_("pl.empty.categoryEmpty")}</p>
          <p class="empty-desc">{$_("pl.empty.categoryEmptyDesc")}</p>
          <button
            class="btn btn--primary"
            disabled={!canEditProducts}
            title={!canEditProducts ? $_("permissions.readOnly") : undefined}
            onclick={() => push(`/t/${slug}/products/new?category_id=${selectedSubcategory ?? selectedCategory}`)}
          >
            <span class="material-icons-outlined">add</span>
            {$_("pl.empty.addToCategory")}
          </button>
          <button class="btn btn--secondary" onclick={clearFilters}>{$_("pl.empty.clearFilters")}</button>
        </div>
      {:else if hasFilters}
        <div class="empty-state card">
          <span class="material-icons-outlined empty-icon">search_off</span>
          <p>{$_("pl.empty.noMatches")}</p>
          <div class="empty-actions">
            {#if selectedSupplier && canEditProducts}
              <!-- A supplier with no cards yet — typically one recorded by
                   hand: its catalogue is filled from here. -->
              <button class="btn btn--primary" onclick={() => push(`/t/${slug}/products/new${supplierQs}`)}>
                <span class="material-icons-outlined">add</span>
                {$_("pl.empty.addLink")}
              </button>
              <button class="btn btn--secondary" onclick={() => push(`/t/${slug}/products/import${supplierQs}`)}>
                <span class="material-icons-outlined">upload_file</span>
                {$_("pl.toolbar.import")}
              </button>
            {/if}
            <button class="btn btn--secondary" onclick={clearFilters}>{$_("pl.empty.clearFilters")}</button>
          </div>
        </div>
      {:else}
        <div class="empty-state card empty-state--onboarding">
          <span class="material-icons-outlined empty-icon">inventory_2</span>
          <p class="empty-title">{$_("pl.empty.title")}</p>
          <p class="empty-desc">{$_("pl.empty.desc")}</p>
          <p class="empty-desc">
            {$_("pl.empty.needsCategories")}
            <a href={`#/t/${slug}/product-categories`}>{$_("pl.empty.categoriesLink")}</a>
          </p>
          <div class="empty-actions">
            <button class="btn btn--primary" onclick={() => push(`/t/${slug}/products/new`)} disabled={!canEditProducts} title={!canEditProducts ? $_("permissions.readOnly") : undefined}>
              <span class="material-icons-outlined">add</span>
              {$_("pl.empty.addLink")}
            </button>
            <button class="btn btn--secondary" onclick={() => push(`/t/${slug}/products/import`)} disabled={!canEditProducts} title={!canEditProducts ? $_("permissions.readOnly") : undefined}>
              <span class="material-icons-outlined">upload_file</span>
              {$_("pl.toolbar.import")}
            </button>
          </div>
        </div>
      {/if}
    {:else if layout === "catalog"}
      <!-- A tile opens the card, as the eye does in the table. -->
      <div class="pc-grid">
        {#each sortedProducts as p (p.id)}
          <a class="pc-card" class:inactive={!p.is_active} href={`#/t/${slug}/products/${p.id}`}>
            {#if p.primary_image_url}
              <img class="pc-img" src={p.primary_tile_url ?? p.primary_image_url} alt="" loading="lazy" />
            {:else}
              <span class="pc-img pc-img--none">{$_("sc.product.noImage")}</span>
            {/if}
            <span class="pc-body">
              <span class="pc-name" title={p.name}>{p.name}</span>
              {#if p.supplier_gap_reason}<SupplierGapBadge reason={p.supplier_gap_reason} />{/if}
              <span class="pc-code">{p.ean ? formatBarcode(p.ean) : p.sku}</span>
              <span class="pc-price-row">
                {#if Number(p.price_eur) > 0}
                  <span class="pc-price">{formatEur(p.price_eur, priceDecimals(slug))}</span>
                {:else}
                  <span class="pc-price">{formatEur(0, priceDecimals(slug))}</span>
                  <span class="pc-muted">{$_("sc.priceRequired")}</span>
                {/if}
              </span>
              <span class="pc-muted">{$_("pl.col.stock")}: {Number(p.total_stock ?? 0)}</span>
              <span class="pc-open">{$_("pl.action.view")}</span>
            </span>
          </a>
        {/each}
      </div>
    {:else}
      <div class="card table-card">
        <table class="pl-table col-divided">
          <thead>
            <tr>
              <th style="width: 76px" class="th-tick" title={$_("pl.col.onSite.hint")}>
                <span class="th-all">
                  <input
                    type="checkbox"
                    class="row-tick"
                    checked={allOnSite}
                    use:indeterminate={someOnSite}
                    disabled={bulkBusy || siteRows.length === 0 || !canAction($auth,slug,"assortment.manage",["products","edit"])}
                    onchange={(e) => tickAll("public_visible", e)}
                    title={allOnSite ? $_("pl.tickAll.off") : $_("pl.tickAll.on")}
                    aria-label={allOnSite ? $_("pl.tickAll.off") : $_("pl.tickAll.on")}
                  />{$_("pl.col.onSite")}
                </span>
              </th>
              <th style="width: 76px" class="th-tick" title={$_("pl.col.orderable.hint")}>
                <span class="th-all">
                  <input
                    type="checkbox"
                    class="row-tick"
                    checked={allOrderable}
                    use:indeterminate={someOrderable}
                    disabled={bulkBusy || sortedProducts.length === 0 || !canAction($auth,slug,"assortment.manage",["products","edit"])}
                    onchange={(e) => tickAll("is_orderable", e)}
                    title={allOrderable ? $_("pl.tickAll.off") : $_("pl.tickAll.on")}
                    aria-label={allOrderable ? $_("pl.tickAll.off") : $_("pl.tickAll.on")}
                  />{$_("pl.col.orderable")}
                </span>
              </th>
              {#each ORDERED_COLS as col}
                {#if colOn(col.key)}
                  <!-- A long caption («Штук в транспортной коробке») is cut to
                       one line and says the whole of itself on hover, so one
                       wordy column can no longer set the height of the header
                       row (owner, 2026-09-16). -->
                  <th
                    class:text-right={col.key === "priceEur"}
                    style={colWidths.style(col.key)}
                    title={col.label}
                    aria-sort={sortKey === col.key ? (sortDir === "asc" ? "ascending" : "descending") : undefined}
                  >{#if col.key === "image"}<span class="th-label">{col.label}</span>{:else}<button
                      type="button"
                      class="sort-btn"
                      class:active={sortKey === col.key}
                      onclick={() => sortBy(col.key)}
                    ><span class="th-label">{col.label}</span><span
                        class="material-icons-outlined sort-icon"
                        aria-hidden="true">{sortIcon(col.key)}</span></button>{/if}{#if col.key === "group"}
                      <InfoTooltip text={$_("pl.category.help")} />{/if}<span
                      class="col-grip"
                      aria-hidden="true"
                      use:colWidths.grip={col.key}
                    ></span></th>
                {/if}
              {/each}
              {#if editing}
                <th style="width: 56px" class="text-center th-tick" title={$_("stock.archive.hint")}>{$_("stock.col.archive")}</th>
              {/if}
              <th style="width: 44px" class="text-right" aria-label={$_("pl.action.view")}></th>
            </tr>
          </thead>
          <tbody>
            {#each sortedProducts as p (p.id)}
              {@const upd = updatesByProduct.get(p.id)}
              <tr class:inactive={!p.is_active} class:not-orderable={p.is_orderable === false}>
                <td class="td-toggle text-center">
                  <input
                    type="checkbox"
                    class="row-tick"
                    checked={onSite(p)}
                    disabled={togglingId === p.id || !p.is_active || !canAction($auth,slug,"assortment.manage",["products","edit"])}
                    onchange={(e) => toggleVisibility(p, e)}
                    title={onSite(p) ? $_("pl.toggle.visible") : $_("pl.toggle.hidden")}
                    aria-label={$_("pl.col.onSite.hint")}
                  />
                </td>
                <td class="td-toggle text-center">
                  <input
                    type="checkbox"
                    class="row-tick"
                    checked={p.is_orderable ?? true}
                    disabled={togglingId === p.id || !canAction($auth,slug,"assortment.manage",["products","edit"])}
                    onchange={(e) => toggleOrderable(p, e)}
                    title={(p.is_orderable ?? true) ? $_("pl.orderable.on") : $_("pl.orderable.off")}
                    aria-label={$_("pl.col.orderable.hint")}
                  />
                </td>
                {#each ORDERED_COLS as col}
                  {#if colOn(col.key)}
                    <td
                      class="td-cell"
                      class:text-right={col.key === "priceEur"}
                      class:td-img={col.key === "image"}
                      class:td-text={col.type === "long" || col.type === "multi"}
                      title={col.type === "long" || col.type === "multi" ? cellText(p, col) : undefined}
                    >{@render cell(p, col)}</td>
                  {/if}
                {/each}
                {#if editing}
                  <td class="td-toggle text-center">
                    <input
                      type="checkbox"
                      class="row-tick"
                      checked={!p.is_active}
                      disabled={!canArchiveAny || !canArchive(p)}
                      title={canArchive(p) ? $_("stock.col.archive") : $_("stock.archive.needsZero")}
                      aria-label={$_("stock.col.archive")}
                      onchange={(e) => toggleArchive(p, (e.target as HTMLInputElement).checked)}
                    />
                  </td>
                {/if}
                <!-- The eye opens the card; the card is where it is edited. -->
                <td class="td-actions">
                  <button
                    class="btn-icon"
                    onclick={() => goProduct(p.id)}
                    title={$_("pl.action.view")}
                    aria-label={$_("pl.action.view")}
                  >
                    <span class="material-icons-outlined">visibility</span>
                  </button>
                </td>
              </tr>
              <!-- Notices about this card from its supplier, right under it
                   (spec 20260915 §2.2 / §1.2). -->
              {#if p.supplier_gap_reason && !p.supplier_gap_decided_at && p.is_active}
                <tr class="row-note row-note--gap">
                  <td colspan={tableCols}>
                    <span class="material-icons-outlined" aria-hidden="true">remove_shopping_cart</span>
                    <span class="row-note-text">{gapText(p)}</span>
                    <button
                      type="button"
                      class="row-note-btn row-note-btn--primary"
                      disabled={!canEditProducts}
                      onclick={() => (gapDialogIds = [p.id])}
                    >{$_("gap.row.decide")}</button>
                  </td>
                </tr>
              {:else if p.supplier_back_at}
                <tr class="row-note row-note--back">
                  <td colspan={tableCols}>
                    <span class="material-icons-outlined" aria-hidden="true">add_shopping_cart</span>
                    <span class="row-note-text">{$_("gap.row.back")}</span>
                    <button
                      type="button"
                      class="row-note-btn row-note-btn--primary"
                      disabled={!canEditProducts || rowBusy === p.id}
                      onclick={() => resolveBack(p, true)}
                    >{$_("gap.row.restore")}</button>
                    <button
                      type="button"
                      class="row-note-btn"
                      disabled={!canEditProducts || rowBusy === p.id}
                      onclick={() => resolveBack(p, false)}
                    >{$_("gap.row.dismiss")}</button>
                  </td>
                </tr>
              {/if}
              {#if upd}
                <tr class="row-note row-note--upd">
                  <td colspan={tableCols}>
                    <span class="material-icons-outlined" aria-hidden="true">sync</span>
                    <span class="row-note-text">{$_("upd.row.changed", { values: { fields: changedFields(upd) } })}</span>
                    <button
                      type="button"
                      class="row-note-btn row-note-btn--primary"
                      disabled={!canEditProducts || rowBusy === p.id}
                      onclick={() => resolveRowUpdates(p, "accept")}
                    >{$_("supdir.updates.accept")}</button>
                    <button
                      type="button"
                      class="row-note-btn"
                      disabled={!canEditProducts || rowBusy === p.id}
                      onclick={() => resolveRowUpdates(p, "reject")}
                    >{$_("supdir.updates.reject")}</button>
                    <button type="button" class="row-note-link" onclick={() => (view = "updates")}>
                      {$_("upd.row.details")}
                    </button>
                  </td>
                </tr>
              {/if}
            {/each}
          </tbody>
        </table>
      </div>

    {/if}
  </div>

  <ColumnSettings
    bind:open={columnsOpen}
    columns={COLUMNS}
    bind:visible={colVisible}
    bind:order={colOrder}
    onchange={onColumnToggle}
  />

  {#if toast}
    <div class="pl-toast">{toast}</div>
  {/if}

  {#if bulkResult}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="bulk-backdrop" onclick={() => (bulkResult = null)}></div>
    <div class="bulk-modal" role="dialog" aria-modal="true" aria-label={$_("pl.bulkPhotos.results.title")}>
      <div class="bulk-head">
        <span class="material-icons-outlined bulk-icon">add_photo_alternate</span>
        <h2 class="bulk-title">{$_("pl.bulkPhotos.results.title")}</h2>
        <button class="bulk-close" onclick={() => (bulkResult = null)} aria-label={$_("common.close")}>
          <span class="material-icons-outlined">close</span>
        </button>
      </div>
      <p class="bulk-summary">
        {$_("pl.bulkPhotos.result", {
          values: { attached: bulkResult.attached.length, skipped: bulkResult.skipped.length },
        })}
      </p>
      {#if bulkResult.skipped.length > 0}
        <div class="bulk-skipped">
          <div class="bulk-skipped-head">{$_("pl.bulkPhotos.results.skippedTitle")}</div>
          <ul class="bulk-skipped-list">
            {#each bulkResult.skipped as s}
              <li>
                <span class="bulk-file">{s.file}</span>
                <span class="bulk-reason">{skipReasonLabel(s.reason)}</span>
              </li>
            {/each}
          </ul>
        </div>
      {/if}
      <div class="bulk-actions">
        <button class="btn btn--primary" onclick={() => (bulkResult = null)}>{$_("common.close")}</button>
      </div>
    </div>
  {/if}
  {#if gapDialogIds}
    <SupplierGapDialog
      {slug}
      productIds={gapDialogIds}
      onclose={(changed) => {
        gapDialogIds = null;
        if (changed) load();
      }}
    />
  {/if}
</AppShell>

<!-- Hover preview for the thumbnail column. Fixed-positioned and rendered
     outside the table so the card's horizontal scroll can't clip it. -->
{#if imgPreview}
  <div class="img-preview" style="left: {imgPreview.x}px; top: {imgPreview.y}px;">
    <img src={imgPreview.url} alt="" />
  </div>
{/if}

{#snippet cell(p: ProductListItem, col: Col)}
  {#if col.key === "image"}
    {#if p.primary_image_url}
      <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
      <img
        src={p.primary_thumb_url ?? p.primary_image_url}
        alt=""
        loading="lazy"
        onmouseenter={(e) => showImgPreview(e, p.primary_image_url!)}
        onmouseleave={hideImgPreview}
      />
    {:else}<div class="placeholder">📦</div>{/if}
  {:else if col.key === "stock"}
    <span class="stock-badge" class:low={p.total_stock <= 10} class:out={p.total_stock <= 0}>{p.total_stock}</span>
  {:else if col.type === "bool"}
    <!-- A tick is its own editor: clicked, saved. -->
    <input
      type="checkbox"
      class="cell-check"
      checked={!!(p as Record<string, unknown>)[col.field ?? ""]}
      disabled={!canEditProducts || !editing}
      aria-label={col.label}
      onchange={(e) => saveField(p, col.field ?? "", e.currentTarget.checked)}
    />
  {:else if editCell?.id === p.id && editCell?.key === col.key}
    {#if col.key === "group"}
      <!-- The tree built in Product Categories, top level. Picking a group
           files the product on it (leaf semantics) and clears the sub. -->
      <select
        class="cell-input"
        use:focusSelect
        onblur={cancelEdit}
        onchange={(e) => {
          const v = parseInt((e.target as HTMLSelectElement).value, 10);
          commitCategory(p, isNaN(v) ? null : v, null);
        }}
      >
        <option value="" selected={groupIdOf(p) == null}>-</option>
        {#each parentCategories as cat}
          <option value={cat.id} selected={groupIdOf(p) === cat.id}>{cat.name}</option>
        {/each}
      </select>
    {:else if col.key === "subgroup"}
      <select
        class="cell-input"
        use:focusSelect
        onblur={cancelEdit}
        disabled={groupIdOf(p) == null}
        onchange={(e) => {
          const v = parseInt((e.target as HTMLSelectElement).value, 10);
          commitCategory(p, groupIdOf(p), isNaN(v) ? null : v);
        }}
      >
        <option value="" selected={subIdOf(p) == null}>-</option>
        {#each categories.filter((c) => c.parent_id === groupIdOf(p)) as sub}
          <option value={sub.id} selected={subIdOf(p) === sub.id}>{sub.name}</option>
        {/each}
      </select>
    {:else if col.key === "supplier" && dirSuppliers.length > 0}
      <!-- Suppliers are picked from «Мои поставщики», not typed — create the
           record there first, then attach it to cards here. A legacy free-text
           value not in the directory stays selectable so it isn't lost. -->
      <select
        class="cell-input"
        use:focusSelect
        onblur={cancelEdit}
        onchange={(e) => {
          editVal = (e.target as HTMLSelectElement).value;
          commitEdit(p, col);
        }}
      >
        <option value="" selected={!p.supplier}>-</option>
        {#if p.supplier && !dirSuppliers.includes(p.supplier)}
          <option value={p.supplier} selected>{p.supplier}</option>
        {/if}
        {#each dirSuppliers as name (name)}
          <option value={name} selected={p.supplier === name}>{name}</option>
        {/each}
      </select>
    {:else if col.type === "select" || col.type === "country" || col.type === "pallet"}
      {@const current = String((p as Record<string, unknown>)[col.field ?? ""] ?? "")}
      {@const opts =
        col.type === "country"
          ? [{ value: "", label: "-" }, ...countryOptions]
          : col.type === "pallet"
            ? [
                { value: "", label: "-" },
                ...palletTypesRef
                  .filter((pt) => pt.enabled !== false || String(pt.id) === current)
                  .map((pt) => ({ value: String(pt.id), label: palletLabel(pt) })),
              ]
            : (col.options ?? [])}
      <select
        class="cell-input"
        use:focusSelect
        onblur={cancelEdit}
        onchange={(e) => {
          editVal = (e.target as HTMLSelectElement).value;
          commitEdit(p, col);
        }}
      >
        <!-- A legacy value the list no longer offers stays selectable. -->
        {#if current && !opts.some((o) => o.value === current)}
          <option value={current} selected>{col.key === "palletWeight" ? `${Number(current)} kg` : current}</option>
        {/if}
        {#each opts as o (o.value)}
          <option value={o.value} selected={o.value === current}>{o.label}</option>
        {/each}
      </select>
    {:else if col.type === "long"}
      <!-- Enter saves, Shift+Enter starts a new line, Esc leaves it as it was. -->
      <textarea
        class="cell-input cell-long"
        rows="4"
        bind:value={editVal}
        use:focusOnMount
        onblur={() => commitEdit(p, col)}
        onkeydown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); }
          else if (e.key === "Escape") cancelEdit();
        }}
      ></textarea>
    {:else if col.type === "multi"}
      <span class="cell-val">{cellText(p, col)}</span>
      <!-- Fixed to the viewport like the photo preview, so the table's own
           scroll can't clip it. Leaving it — Done, a click elsewhere, Tab
           out — saves the ticks; Esc drops them. -->
      <div
        class="multi-pop"
        style="left: {popAt.x}px; top: {popAt.y}px;"
        role="dialog"
        aria-label={col.label}
        tabindex="-1"
        use:focusFirst
        onkeydown={(e) => { if (e.key === "Escape") cancelEdit(); }}
        onfocusout={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) commitEdit(p, col);
        }}
      >
        <div class="multi-list">
          {#each refsOf(col) as r (r.id)}
            <label class="multi-row">
              <input
                type="checkbox"
                checked={editIds.includes(r.id)}
                onchange={(e) => toggleId(r.id, e.currentTarget.checked)}
              />
              <span>{refName(col, r)}</span>
            </label>
          {/each}
        </div>
        <div class="multi-actions">
          <button type="button" class="btn btn--primary" onclick={() => commitEdit(p, col)}>{$_("common.done")}</button>
        </div>
      </div>
    {:else if col.type === "num" || col.type === "dec"}
      <input
        class="cell-input"
        type="number"
        step={col.type === "dec" ? "any" : "1"}
        bind:value={editVal}
        use:focusOnMount
        onblur={() => commitEdit(p, col)}
        onkeydown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); else if (e.key === "Escape") cancelEdit(); }}
      />
    {:else}
      <input
        class="cell-input"
        type="text"
        bind:value={editVal}
        placeholder={col.type === "langs" ? "EE, LV, LT" : undefined}
        use:focusOnMount
        onblur={() => commitEdit(p, col)}
        onkeydown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); else if (e.key === "Escape") cancelEdit(); }}
      />
    {/if}
  {:else}
    <span
      class="cell-val"
      class:editable={isEditable(col)}
      class:name={col.key === "name"}
      role="button"
      tabindex={isEditable(col) ? 0 : -1}
      onclick={(e) => startEdit(p, col, e.currentTarget as HTMLElement)}
      onkeydown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          startEdit(p, col, e.currentTarget as HTMLElement);
        }
      }}
    >{cellText(p, col)}{#if col.key === "name" && p.supplier_gap_reason}<SupplierGapBadge
        reason={p.supplier_gap_reason}
      />{/if}{#if col.key === "name" && p.canonical_state === "delisted" && !p.supplier_gap_reason}<span
        class="delisted-badge"
        title={p.canonical_partner_note
          ? `${$_("supdir.badge.delistedHint")} - ${p.canonical_partner_note}`
          : $_("supdir.badge.delistedHint")}
      >{$_("supdir.badge.delisted")}</span>{/if}{#if col.key === "name" && p.supply_state === "sell_off"}<span
        class="selloff-badge"
        title={$_("pl.badge.sellOffHint")}
      >{$_("pl.badge.sellOff")}</span>{/if}{#if col.key === "name" && p.canonical_state === "linked" && p.canonical_partner_note}<span
        class="material-icons-outlined pnote-icon"
        title={p.canonical_partner_note}
      >chat_bubble_outline</span>{/if}</span>
  {/if}
{/snippet}

<style>
  .pl-page { display: flex; flex-direction: column; gap: 16px; }

  /* Page tabs (list · supplier updates) — same pill tabs as the Orders page. */
  .pl-tabs { display: flex; gap: 6px; flex-wrap: wrap; }
  .pl-tab { display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 14px; border: 1px solid var(--color-divider); background: #fff;
    border-radius: 999px; font-size: 12px; font-weight: 600; cursor: pointer;
    color: var(--color-text-muted); transition: background 0.1s, color 0.1s, border-color 0.1s;
    font-family: inherit; }
  .pl-tab:hover { color: var(--color-primary); border-color: var(--color-primary); }
  .pl-tab.active { background: var(--color-primary); color: #fff; border-color: var(--color-primary); }
  .pl-tab .material-icons-outlined { font-size: 16px; }
  .pl-tab-badge { background: #fb8c00; color: #fff; border-radius: 9px; padding: 0 6px;
    font-size: 11px; font-weight: 700; }
  .header-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }

  /* The page's own options, on the second filter row. */
  .opt-select { padding: 7px 10px; border: 1px solid var(--color-divider); border-radius: 6px; font-family: inherit; font-size: 13px; background: #fff; }

  /* Склад's table, given to every view of the section (owner, 2026-09-19). */
  .table-card { overflow-x: auto; background: #fff; border: 1px solid var(--color-divider); border-radius: 8px; padding: 0; box-shadow: none; }
  .pl-table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 1000px; }
  .pl-table th {
    position: relative;
    background: var(--cat-th-bg); padding: 9px 10px; text-align: left; border-bottom: 1px solid var(--cat-th-line);
    font-weight: 700; color: var(--cat-th-color); font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase;
    white-space: nowrap; line-height: 1.3; vertical-align: middle;
  }
  .sort-btn {
    display: inline-flex; align-items: center; gap: 2px; padding: 0; border: none; background: none;
    font: inherit; text-transform: inherit; letter-spacing: inherit; color: inherit; cursor: pointer;
  }
  .sort-btn:hover, .sort-btn.active { color: var(--color-primary); }
  .sort-icon { font-size: 13px !important; opacity: 0.45; }
  .sort-btn.active .sort-icon { opacity: 1; }
  /* Cut, not wrapped; the full caption is the th's title. */
  /* …unless the user widened the column by hand. */
  .pl-table th[style*="width"] .th-label { max-width: none; }
  .th-label { display: inline-block; max-width: 112px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: bottom; }
  /* Row padding is a quarter tighter than it was (10px → 7.5px): 27 products
     were taking two screens with most of the height being air. */
  .pl-table td { padding: var(--cat-cell-y) var(--cat-cell-x); border-bottom: 1px solid var(--color-divider); vertical-align: middle; line-height: 1.25; }
  .pl-table tr:hover td { background: rgba(33,133,213,0.03); }
  .pl-table tr.inactive td { opacity: 0.6; }
  /* Not available to order: grey down the row, the ticks stay clickable. */
  .pl-table tr.not-orderable td { background: #eceff1; }
  /* Muted grey and a size down (owner, 2026-09-22): bright blue ticks down
     two columns merged with the buttons and pulled the eye off the table. */
  .row-tick { width: 13px; height: 13px; margin: 0; cursor: pointer; accent-color: #8a96a3; }
  .row-tick:disabled { cursor: default; }
  .th-tick { white-space: normal; line-height: 1.15; }
  .th-all { display: inline-flex; align-items: center; gap: 5px; }
  .th-all .row-tick { flex: none; margin: 0; }

  .delisted-badge { display: inline-block; margin-left: 6px; padding: 1px 8px; border-radius: 10px; font-size: 10px; font-weight: 600; background: #fdecea; color: #c0392b; white-space: nowrap; }
  /* Sell-off leftovers of an ended partnership: sellable, never reorderable. */
  .selloff-badge { display: inline-block; margin-left: 6px; padding: 1px 8px; border-radius: 10px; font-size: 10px; font-weight: 600; background: #fff3e0; color: #b26a00; white-space: nowrap; }
  /* The supplier left a comment on their card («будет готова к 01.10»). */
  .pnote-icon { margin-left: 6px; font-size: 14px; color: var(--color-primary); vertical-align: middle; cursor: help; }

  /* Supplier notices under a row: position no longer offered (red), offered
     again (green), card edited upstream (blue). */
  .row-note td { padding: 6px 12px 8px 72px; font-size: 12.5px; line-height: 1.6; }
  .row-note .material-icons-outlined { font-size: 16px; margin-right: 6px; vertical-align: -3px; }
  .row-note--gap td { background: #fdf3f2; color: #8e2a1f; }
  .row-note--back td { background: #eef7ee; color: #2e6b35; }
  .row-note--upd td { background: #eef5fc; color: #1d5a91; }
  .row-note-text { margin-right: 10px; }
  .row-note-btn {
    border: 1px solid currentColor; background: #fff; color: inherit; padding: 2px 10px;
    border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; margin-right: 6px; font-family: inherit;
  }
  .row-note-btn--primary { background: var(--color-primary); border-color: var(--color-primary); color: #fff; }
  .row-note-btn:disabled { opacity: 0.5; cursor: default; }
  .row-note-link { border: none; background: none; color: inherit; text-decoration: underline; cursor: pointer; font-size: 12px; font-family: inherit; padding: 0; }
  .gap-filter {
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
    background: #fdf3f2; border: 1px solid #f5c6cb; border-left: 4px solid #c0392b;
    border-radius: var(--radius); padding: 8px 12px; font-size: 13.5px; color: #8e2a1f;
  }
  .gap-filter-text { flex: 1; min-width: 200px; }
  @media (max-width: 640px) {
    .row-note td { padding-left: 12px; }
  }

  /* One switch, so the column is the width of one icon. */
  .td-toggle { width: 44px; white-space: nowrap; }

  .td-img img { width: 32px; height: 32px; object-fit: contain; border-radius: 4px; background: #fff; border: 1px solid #eee; cursor: zoom-in; }
  .td-img img:hover { border-color: var(--color-primary); }

  .img-preview {
    position: fixed; z-index: 1200; pointer-events: none;
    width: 320px; height: 320px;
    background: #fff; border: 1px solid var(--color-divider); border-radius: 8px;
    box-shadow: 0 12px 36px rgba(0,0,0,0.22);
    padding: 8px; box-sizing: border-box;
    display: flex; align-items: center; justify-content: center;
  }
  .img-preview img { max-width: 100%; max-height: 100%; object-fit: contain; }

  /* Mandatory/All preset switch */
  .preset-switch { display: inline-flex; border: 1px solid var(--color-divider); border-radius: 6px; overflow: hidden; }
  .preset-switch button { border: none; background: #fff; padding: 8px 12px; font-size: 13px; font-weight: 600; color: var(--color-text-muted); cursor: pointer; }
  .preset-switch button + button { border-left: 1px solid var(--color-divider); }
  .preset-switch button:hover { background: var(--color-bg); }
  .preset-switch button.active { background: var(--color-primary); color: #fff; }
  .preset-switch button { display: inline-flex; align-items: center; gap: 6px; }
  .preset-switch .material-icons-outlined { font-size: 17px; }

  /* «Каталог»: the tiles of a supplier's catalogue (SupplierCatalog). */
  .pc-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
  .pc-card {
    display: flex; flex-direction: column; overflow: hidden; background: #fff;
    border: 1px solid var(--color-divider); border-radius: var(--radius);
    color: inherit; text-decoration: none; transition: box-shadow 0.15s;
  }
  .pc-card:hover { box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08); }
  .pc-card.inactive { opacity: 0.55; }
  .pc-img { display: block; width: 100%; height: 160px; object-fit: contain; background: #f0f0f0; }
  .pc-img--none { display: flex; align-items: center; justify-content: center; font-size: 12px; color: #aaa; }
  .pc-body { display: flex; flex: 1; flex-direction: column; align-items: flex-start; gap: 6px; padding: 12px 14px; }
  .pc-name {
    max-width: 100%; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;
    font-size: 14px; font-weight: 700; color: var(--color-surface-darker);
  }
  .pc-code { font-family: monospace; font-size: 11px; color: var(--color-text-muted); }
  .pc-price-row { display: flex; align-items: baseline; gap: 6px; margin-top: auto; }
  .pc-price { font-size: 16px; font-weight: 800; color: var(--color-primary); }
  .pc-muted { font-size: 12px; color: var(--color-text-muted); }
  .pc-open {
    align-self: stretch; margin-top: 6px; padding: 7px 12px; border-radius: var(--radius);
    background: var(--color-primary); color: #fff; font-size: 12px; font-weight: 600; text-align: center;
  }
  .pc-card:hover .pc-open { opacity: 0.9; }

  /* Inline-editable cells */
  .td-cell { white-space: nowrap; }
  .td-text { max-width: 260px; overflow: hidden; text-overflow: ellipsis; }
  .td-text .cell-val { display: block; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
  .cell-val { display: inline-block; min-height: 18px; min-width: 24px; }
  /* The name reads in the body weight — bold down a whole column was
     heavier than the table around it (owner, 2026-09-22). */
  .cell-val.editable { cursor: text; border-radius: 3px; padding: 2px 4px; margin: -2px -4px; }
  .cell-val.editable:hover { background: rgba(33,133,213,0.08); box-shadow: inset 0 0 0 1px var(--color-divider); }
  .cell-input { width: 100%; min-width: 70px; box-sizing: border-box; padding: 4px 6px; border: 1px solid var(--color-primary); border-radius: 4px; font-size: 13px; }
  .cell-long { min-width: 240px; font-family: inherit; line-height: 1.35; resize: vertical; white-space: pre-wrap; }
  .cell-check { width: 16px; height: 16px; accent-color: var(--color-primary); cursor: pointer; }
  .cell-check:disabled { cursor: default; }
  .td-img .placeholder { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; background: #f0f0f0; border-radius: 4px; opacity: 0.5; font-size: 20px; }

  /* Allergens / standards / label icons: ticked in a popover over the table. */
  .multi-pop {
    position: fixed; z-index: 1150; width: 300px; max-height: 340px;
    display: flex; flex-direction: column; background: #fff;
    border: 1px solid var(--color-divider); border-radius: 8px;
    box-shadow: 0 12px 36px rgba(0,0,0,0.2); white-space: normal; cursor: default;
  }
  .multi-pop:focus { outline: none; }
  .multi-list { overflow-y: auto; padding: 6px; }
  .multi-row { display: flex; align-items: center; gap: 8px; padding: 5px 6px; border-radius: 4px; font-size: 13px; cursor: pointer; }
  .multi-row:hover { background: var(--color-bg); }
  .multi-row input { width: 15px; height: 15px; accent-color: var(--color-primary); flex-shrink: 0; }
  .multi-actions { display: flex; justify-content: flex-end; padding: 8px; border-top: 1px solid var(--color-divider); }

  .stock-badge { display: inline-block; padding: 2px 6px; border-radius: 4px; background: #e8f5e9; color: #2e7d32; font-weight: 600; font-size: 12px; }
  .stock-badge.low { background: #fff3e0; color: #e65100; }
  .stock-badge.out { background: #ffebee; color: #c62828; }

  .text-right, .pl-table th.text-right { text-align: right; }
  .text-center { text-align: center; }

  .td-actions { text-align: right; white-space: nowrap; }
  .visually-hidden-input { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
  .btn-icon {
    border: none; background: none; cursor: pointer; padding: 4px;
    color: var(--color-text-muted); border-radius: 4px; vertical-align: middle;
  }
  .btn-icon:hover { color: var(--color-primary); background: rgba(33,133,213,0.08); }
  .btn-icon .material-icons-outlined { font-size: 20px; }

  .loading-state, .empty-state { padding: 64px 32px; text-align: center; color: var(--color-text-muted); }
  .empty-state { display: flex; flex-direction: column; align-items: center; gap: 12px; }
  .empty-icon { font-size: 48px !important; opacity: 0.4; }
  .empty-state--onboarding { padding: 72px 32px; }
  .empty-title { font-size: 16px; font-weight: 700; color: var(--color-surface-darker); margin: 0; }
  .empty-desc { margin: 0; max-width: 360px; }
  .empty-state .btn { margin-top: 8px; display: inline-flex; align-items: center; gap: 6px; }
  .empty-actions { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; }
  .empty-desc a { color: var(--color-primary); }

  /* Whole-page photo drop target. */
  .drop-overlay {
    position: fixed; inset: 0; z-index: 1200;
    background: rgba(33, 133, 213, 0.12);
    display: flex; align-items: center; justify-content: center;
    pointer-events: none;
    border: 3px dashed var(--color-primary);
  }
  .drop-overlay-inner {
    background: #fff; border-radius: 12px; padding: 28px 40px; text-align: center;
    box-shadow: 0 8px 40px rgba(0,0,0,0.18);
  }
  .drop-icon { font-size: 42px !important; color: var(--color-primary); }
  .drop-title { margin: 8px 0 4px; font-size: 16px; font-weight: 700; color: var(--color-surface-darker); }
  .drop-hint { margin: 0; font-size: 13px; color: var(--color-text-muted); max-width: 380px; }

  .upload-progress {
    position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
    background: var(--color-surface-darker); color: #fff; padding: 10px 20px;
    border-radius: 6px; font-size: 13px; z-index: 1300; box-shadow: 0 4px 12px rgba(0,0,0,0.25);
  }

  /* Per-file results of a batch upload. */
  .bulk-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 1400; }
  .bulk-modal {
    position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%); z-index: 1401;
    background: #fff; border-radius: 12px; box-shadow: 0 8px 40px rgba(0,0,0,0.18);
    width: 560px; max-width: calc(100vw - 32px); max-height: calc(100vh - 60px);
    display: flex; flex-direction: column; gap: 14px; padding: 24px; overflow: hidden;
  }
  .bulk-head { display: flex; align-items: center; gap: 10px; }
  .bulk-icon { font-size: 22px !important; color: var(--color-primary); }
  .bulk-title { margin: 0; font-size: 16px; font-weight: 700; flex: 1; }
  .bulk-close { border: none; background: none; cursor: pointer; color: var(--color-text-muted); padding: 4px; border-radius: 4px; display: flex; }
  .bulk-close:hover { background: #f0f0f0; }
  .bulk-summary { margin: 0; font-size: 13.5px; color: #555; }
  .bulk-skipped { display: flex; flex-direction: column; gap: 6px; overflow: hidden; }
  .bulk-skipped-head { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--color-text-muted); }
  .bulk-skipped-list {
    margin: 0; padding: 0; list-style: none; overflow-y: auto;
    border: 1px solid var(--color-divider); border-radius: 8px;
  }
  .bulk-skipped-list li {
    display: flex; justify-content: space-between; gap: 12px;
    padding: 7px 12px; font-size: 12.5px; border-bottom: 1px solid #f2f2f2;
  }
  .bulk-skipped-list li:last-child { border-bottom: none; }
  .bulk-file { font-family: monospace; color: var(--color-surface-darker); word-break: break-all; }
  .bulk-reason { color: #c0392b; white-space: nowrap; }
  .bulk-actions { display: flex; justify-content: flex-end; }

  .pl-toast {
    position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
    background: var(--color-surface-darker); color: #fff;
    padding: 12px 20px; border-radius: 6px; font-size: 14px;
    box-shadow: 0 6px 24px rgba(0,0,0,0.25); z-index: 1100;
  }

  /* ── Phone ── */
  @media (max-width: 700px) {
    .header-actions { width: 100%; gap: 8px; }
    /* The actions share the row, so every one — Import included — stays on
       screen without a sideways scroll. */
    .header-actions > .btn { flex: 1 1 auto; justify-content: center; }
    .preset-switch { flex: 1 1 100%; }
    .preset-switch button { flex: 1; }
  }
</style>
